import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaWeight, FaPlus, FaTimes, FaLock, FaLockOpen,
  FaEdit, FaTrash, FaArrowUp, FaArrowDown, FaExclamationTriangle,
  FaFileExcel, FaFilePdf, FaDownload, FaEye, FaSave, FaEllipsisV,
  FaChevronRight, FaChevronDown, FaHistory, FaCalendarAlt, FaSearch
} from 'react-icons/fa';
import './css_pages/Inventories.css';
import {
  getAllInventories, getInventoryById, createInventory,
  updateInventory, deleteInventory, toggleLockInventory,
  importInventoryFromExcel,
  getInventoriesByLocation, getInventoryProducts
} from '../services/inventoriesService';
import { getAllLocations } from '../services/locationsService';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import api from '../services/api';

const formatDateTimeCA = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr.replace('Z', '').replace('T', ' '));
  const date = d.toLocaleDateString('en-CA', { month: 'long', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date}, ${time}`;
};

// Solo fecha (sin hora) — para el encabezado del catálogo agrupado por día.
const formatDateOnlyCA = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { month: 'long', day: '2-digit', year: 'numeric' });
};

const groupByCategory = (items) =>
  items.reduce((acc, item) => {
    const cat = item.category_name || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

const groupByTypeAndCategory = (items) =>
  items.reduce((acc, item) => {
    const type = item.product_type_name || 'Other';
    const cat  = item.category_name     || 'Uncategorized';
    if (!acc[type])      acc[type]      = {};
    if (!acc[type][cat]) acc[type][cat] = [];
    acc[type][cat].push(item);
    return acc;
  }, {});

// Search Product: formato de cantidades y totales por tipo de peso.
// Las cantidades se muestran tal cual se capturaron (g, Bottle, Each...), sin convertir,
// por eso los totales se separan por tipo de peso (no se mezclan gramos con botellas).
const fmtSearchQty = (n) => {
  if (n === null || n === undefined) return '—';
  const v = parseFloat(n);
  if (isNaN(v)) return '—';
  return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

const computeSearchTotals = (locations) => {
  const map = {};
  locations.forEach(l => {
    if (l.quantity !== null && l.quantity !== undefined) {
      const u = l.weight_type || '—';
      map[u] = map[u] || { last: 0, qty: 0 };
      map[u].qty += parseFloat(l.quantity) || 0;
    }
    if (l.last_quantity !== null && l.last_quantity !== undefined) {
      const u = l.last_weight_type || l.weight_type || '—';
      map[u] = map[u] || { last: 0, qty: 0 };
      map[u].last += parseFloat(l.last_quantity) || 0;
    }
  });
  return Object.entries(map).map(([unit, t]) => ({
    unit,
    last: Math.round(t.last * 1000) / 1000,
    qty:  Math.round(t.qty  * 1000) / 1000,
  }));
};

// Columnas "Product Wt" y "Percentage" de la tabla de productos: ocultas por pedido.
// Los cálculos siguen corriendo por debajo; para volver a mostrarlas basta con ponerlo en true.
const SHOW_WEIGHT_PCT_COLUMNS = false;

function Inventories() {
  const [inventories, setInventories] = useState([]);
  const [products, setProducts]       = useState([]);
  const [preps, setPreps]             = useState([]);
  const [locations, setLocations]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [searchTerm, setSearchTerm]   = useState('');
  const [filterDate, setFilterDate]   = useState('');
  // Catálogo agrupado por fecha: qué fechas están expandidas (todas colapsadas al abrir)
  const [expandedDates, setExpandedDates] = useState({});

  const [showCreateModal, setShowCreateModal]   = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [showViewModal, setShowViewModal]       = useState(false);
  const [viewInventory, setViewInventory]       = useState(null);
  const [editingId, setEditingId]               = useState(null);
  const [deleteId, setDeleteId]                 = useState(null);
  const [productSearch, setProductSearch]       = useState('');
  const [prepSearch, setPrepSearch]             = useState('');
  const [selectingFor, setSelectingFor]         = useState(null);
  const [loadingLastInventory, setLoadingLastInventory] = useState(false);

  // Candado síncrono anti doble-tap para Save / Save & Close.
  // useRef se actualiza al instante (a diferencia de useState + loading),
  // así que evita que un doble-tap en móvil dispare dos guardados en paralelo
  // y termine creando inventarios duplicados.
  const isSavingRef = useRef(false);

  // ── Load inventory selector ──────────────────────────────────
  const [availableInventories, setAvailableInventories]   = useState([]);
  const [selectedInventoryId, setSelectedInventoryId]     = useState('');
  // Fecha seleccionada para el flujo "Uncounted by date" (toda la tienda)
  const [uncountedDate, setUncountedDate]                 = useState('');

  // ── Search Product (modal) ────────────────────────────────────────────────
  const [showSearchModal, setShowSearchModal]   = useState(false);
  const [searchDate, setSearchDate]             = useState('');
  const [searchQuery, setSearchQuery]           = useState('');
  const [searchResults, setSearchResults]       = useState(null);   // null = aún no se busca
  const [searchPrevDate, setSearchPrevDate]     = useState(null);
  const [searchLoading, setSearchLoading]       = useState(false);
  const [searchError, setSearchError]           = useState('');
  const searchReqRef                            = useRef(0);
  const [loadingInventoryList, setLoadingInventoryList]   = useState(false);

  // ── Picker inline weights state ─────────────────────────────
  // { [productId]: { weightType: 'g', currentWeight: '' } }
  const [pickerWeights, setPickerWeights]         = useState({});
  // productId del producto actualmente expandido en el picker
  const [pickerExpandedId, setPickerExpandedId]   = useState(null);
  // Tab activo en el modal unificado: 'products' | 'preps'
  const [pickerTab, setPickerTab]                 = useState('products');
  // Estado del picker de preps: expandido y cantidad
  // { [prepId]: { currentQty: '' } }
  const [pickerPrepQty, setPickerPrepQty]         = useState({});
  const [pickerPrepExpandedId, setPickerPrepExpandedId] = useState(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile]           = useState(null);
  const [importing, setImporting]             = useState(false);
  const [importResult, setImportResult]       = useState(null);

  const [showPrintModal, setShowPrintModal]         = useState(false);
  const [printLocationId, setPrintLocationId]       = useState('');
  const [printExportLoading, setPrintExportLoading] = useState(false);

  const [groupBy, setGroupBy]           = useState('category');
  const [tableGroupBy, setTableGroupBy] = useState('none');

  // ── Mobile overflow menu state ──
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef(null);

  const { selectedStore } = useStore();
  const { isEmployee }    = useAuth();

  // Close mobile menu on outside click
  useEffect(() => {
    if (!showMobileMenu) return;
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMobileMenu]);

  const getCurrentLocalDate = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
  };

  const [formData, setFormData] = useState({
    inventoryDate: getCurrentLocalDate(),
    locationId: null,
    items: [],
    prepItems: [],
    waste: []
  });

  // ── GROUP BY TOGGLE ──────────────────────────────────────────────────────
  const GroupByToggle = ({ value, onChange, showNone = false }) => (
    <div className="invent-groupby-toggle">
      <span className="invent-groupby-label">Group by:</span>
      {showNone && (
        <button className={`invent-toggle-btn ${value === 'none' ? 'invent-active' : ''}`} onClick={() => onChange('none')} type="button">None</button>
      )}
      <button className={`invent-toggle-btn ${value === 'category' ? 'invent-active' : ''}`} onClick={() => onChange('category')} type="button">Product Type</button>
      <button className={`invent-toggle-btn ${value === 'type' ? 'invent-active' : ''}`} onClick={() => onChange('type')} type="button">Category</button>
    </div>
  );

  // ── CÁLCULOS ─────────────────────────────────────────────────────────────
  const GRAMS = {
    g: 1, kg: 1000, oz: 28.3495, lb: 453.592,
    ml: 1, L: 1000, Liter: 1000, Gallon: 3785.41, 'fl oz': 29.5735
  };
  const pureWeightUnits = ['g', 'kg', 'oz', 'lb'];
  const countTypes      = ['Bottle', 'Keg', 'Can', 'Each', 'Box', 'Bag', 'Carton'];

  const calculateValues = (
    currentWeight, weightType, fullWeight, emptyWeight,
    wholesalePrice, containerSize = null, containerUnit = null, containerType = null
  ) => {
    if (!currentWeight || !wholesalePrice) return { productWeight: '', percentage: '', ws: '' };
    const current = parseFloat(currentWeight);
    const price   = parseFloat(wholesalePrice);

    if (pureWeightUnits.includes(weightType)) {
      const currentInGrams =
        weightType === 'kg' ? current * 1000 :
        weightType === 'oz' ? current * 28.3495 :
        weightType === 'lb' ? current * 453.592 : current;

      if (fullWeight != null) {
        const full  = parseFloat(fullWeight);
        const empty = (emptyWeight != null && emptyWeight !== '') ? parseFloat(emptyWeight) : 0;
        const net   = full - empty;
        if (full <= 0 || net <= 0 || price <= 0) return { productWeight: '', percentage: '', ws: '' };
        const pesoProducto = Math.max(0, currentInGrams - empty);
        const porcentaje   = pesoProducto / net;
        return {
          productWeight: pesoProducto.toFixed(2),
          percentage:    (porcentaje * 100).toFixed(2),
          ws:            (porcentaje * price).toFixed(2)
        };
      }
      const contSizeInG = containerSize && containerUnit && GRAMS[containerUnit]
        ? parseFloat(containerSize) * GRAMS[containerUnit]
        : parseFloat(containerSize) || 1;
      if (contSizeInG <= 0) return { productWeight: '', percentage: '', ws: '' };
      const ratio = currentInGrams / contSizeInG;
      return {
        productWeight: currentInGrams.toFixed(2),
        percentage:    (ratio * 100).toFixed(2),
        ws:            (ratio * price).toFixed(2)
      };
    }
    if (countTypes.includes(weightType)) {
      return {
        productWeight: '-',
        percentage:    (current * 100).toFixed(2),
        ws:            (current * price).toFixed(2)
      };
    }
    return { productWeight: '', percentage: '', ws: '' };
  };

  const calculatePrepWsValue = (currentWeight, weightType, yieldQuantity, yieldUnit, totalCost) => {
    const qty      = parseFloat(currentWeight) || 0;
    const yieldQty = parseFloat(yieldQuantity) || 0;
    const cost     = parseFloat(totalCost)     || 0;
    if (qty <= 0 || yieldQty <= 0 || cost <= 0) return '';
    let wsValue;
    if (weightType === yieldUnit) {
      wsValue = (cost / yieldQty) * qty;
    } else {
      const qtyBase   = qty      * (GRAMS[weightType] || 1);
      const yieldBase = yieldQty * (GRAMS[yieldUnit]  || 1);
      wsValue = yieldBase > 0 ? (cost / yieldBase) * qtyBase : 0;
    }
    return wsValue.toFixed(2);
  };

  const validateWeight = () => ({ type: null, message: '' });

  const calculateNetWeight = (item) => {
    const full  = parseFloat(item.fullWeight)  || 0;
    const empty = parseFloat(item.emptyWeight) || 0;
    return (full > 0 && empty >= 0 && full > empty) ? (full - empty).toFixed(2) : null;
  };

  const resolvePrices = (wholesalePrice, caseSize) => {
    const priceContainer = parseFloat(wholesalePrice) || 0;
    const pricePerUnit   = caseSize && parseFloat(caseSize) > 0
      ? priceContainer / parseFloat(caseSize)
      : priceContainer;
    return { priceContainer, pricePerUnit };
  };

  const buildRowFromProduct = (product, displayOrder = 0, overrideWeightType = null, overrideCurrentWeight = '') => {
    const containerType     = product.containerType || 'Bottle';
    const hasWeights        = !!(product.full_weight);
    const initialWeightType = overrideWeightType || (hasWeights ? 'g' : containerType);
    const { priceContainer, pricePerUnit } = resolvePrices(product.wholesalePrice, product.caseSize);
    const isCountInit       = countTypes.includes(initialWeightType);
    const useContainerPrice = !isCountInit && hasWeights;
    const activePrice       = useContainerPrice ? priceContainer : pricePerUnit;

    // Pre-calculate values if currentWeight provided
    const fullWeight  = product.full_weight  ?? null;
    const emptyWeight = product.empty_weight ?? null;
    const containerSize = product.container_size;
    const containerUnit = product.container_unit;
    const calc = overrideCurrentWeight
      ? calculateValues(overrideCurrentWeight, initialWeightType, fullWeight, emptyWeight, activePrice, containerSize, containerUnit, containerType)
      : { productWeight: '', percentage: '', ws: '' };

    return {
      product,
      productId:               product.id,
      productName:             product.name,
      productCode:             product.product_code,
      containerType,
      weightType:              initialWeightType,
      currentWeight:           overrideCurrentWeight,
      caseSize:                product.caseSize || '',
      fullWeight,
      emptyWeight,
      netWeight:               calculateNetWeight({ fullWeight: product.full_weight, emptyWeight: product.empty_weight }),
      productWeight:           calc.productWeight,
      percentage:              calc.percentage,
      wholesalePrice:          activePrice,
      wholesalePriceContainer: priceContainer,
      wholesalePricePerUnit:   pricePerUnit,
      wholesaleValue:          calc.ws,
      weightWarning:           { type: null, message: '' },
      category_name:           product.category_name     || 'Uncategorized',
      product_type_name:       product.product_type_name || 'Other',
      displayOrder
    };
  };

  const buildRowFromPrep = (prep, displayOrder = 0) => ({
    prep,
    prepId:        prep.id,
    prepName:      prep.name,
    yieldQuantity: prep.yield_quantity,
    yieldUnit:     prep.yield_unit     || 'Each',
    yieldUnitCost: prep.yield_unit_cost,
    prepTotalCost: prep.total_cost,
    weightType:    prep.yield_unit     || 'Each',
    currentWeight: '',
    wholesaleValue: '',
    displayOrder
  });

  // ── FETCH ─────────────────────────────────────────────────────────────────
  const fetchInventories = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const response = await getAllInventories(selectedStore?.id);
      if (response.success) setInventories(response.data);
    } catch { setError('Error loading inventories'); }
    finally  { setLoading(false); }
  }, [selectedStore]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await getAllLocations(selectedStore?.id);
      if (response.success) setLocations(response.data);
    } catch (error) { console.error('Error loading locations:', error); }
  }, [selectedStore]);

  const fetchProducts = useCallback(async () => {
    if (!selectedStore) return;
    try {
      const response = await api.get('/inventories/available-products', {
        params: { storeId: selectedStore.id }
      });
      if (response.data.success) {
        setProducts(response.data.data.map(item => ({
          id:                item.id,
          name:              item.name,
          product_code:      item.product_code,
          containerType:     item.container_type,
          container_size:    item.container_size,
          container_unit:    item.container_unit,
          container_size_base_unit: item.container_size_base_unit,
          caseSize:          item.case_size,
          full_weight:       item.full_weight  || null,
          empty_weight:      item.empty_weight || null,
          weight_unit:       'g',
          wholesalePrice:    item.wholesale_price || 0,
          par:               item.par,
          reorder_point:     item.reorder_point,
          lastInvQuantity:   item.last_inv_quantity ?? 0,
          category_name:     item.category_name     || 'Uncategorized',
          product_type_name: item.product_type_name || 'Other'
        })));
        setPreps(response.data.preps || []);
      }
    } catch (error) { console.error('Error loading products:', error); }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) { fetchInventories(); fetchProducts(); fetchLocations(); }
  }, [selectedStore, fetchInventories, fetchLocations, fetchProducts]);

  useEffect(() => {
    const isAnyModalOpen = showCreateModal || showDeleteModal || showProductModal || showImportModal || showViewModal || showPrintModal;
    if (isAnyModalOpen) document.body.classList.add('modal-open');
    else                document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [showCreateModal, showDeleteModal, showProductModal, showImportModal, showViewModal, showPrintModal]);

  // ── VIEW MODAL ───────────────────────────────────────────────────────────
  const handleViewInventory = async (id) => {
    try {
      setLoading(true);
      const response = await getInventoryById(id);
      if (response.success) {
        setViewInventory(response.data);
        setGroupBy('category');
        setShowViewModal(true);
      }
    } catch { alert('Error loading inventory'); }
    finally  { setLoading(false); }
  };

  // ── FORM HANDLERS ────────────────────────────────────────────────────────
  const resetForm = () => setFormData({
    inventoryDate: getCurrentLocalDate(),
    locationId: null,
    items: [],
    prepItems: [],
    waste: []
  });

  const handleOpenCreateModal  = () => {
    resetForm();
    setEditingId(null);
    setTableGroupBy('none');
    setAvailableInventories([]);
    setSelectedInventoryId('');
    setShowCreateModal(true);
  };
  const handleCloseCreateModal = () => { setShowCreateModal(false); resetForm(); setEditingId(null); };

  const handleOpenProductModal = (table, target = null) => {
    setSelectingFor({ table, target });
    setPickerWeights({});
    setPickerExpandedId(null);
    setPickerPrepQty({});
    setPickerPrepExpandedId(null);
    setPickerTab('products');
    setProductSearch('');
    setPrepSearch('');
    setShowProductModal(true);
  };

  // ── PICKER: toggle expand / collapse ──────────────────────────────────────
  const handlePickerToggle = (product) => {
    if (pickerExpandedId === product.id) {
      // colapsar
      setPickerExpandedId(null);
    } else {
      // expandir y pre-poblar weightType si no existe
      setPickerExpandedId(product.id);
      setPickerWeights(prev => {
        if (prev[product.id]) return prev;
        const containerType = product.containerType || 'Bottle';
        const hasWeights    = !!(product.full_weight);
        const defaultWT     = hasWeights ? 'g' : containerType;
        return { ...prev, [product.id]: { weightType: defaultWT, currentWeight: '' } };
      });
    }
  };

  // ── PICKER: avanzar al siguiente producto ────────────────────────────────
  const handlePickerNext = (currentProduct) => {
    const list   = filteredProducts;
    const idx    = list.findIndex(p => p.id === currentProduct.id);
    const next   = list[idx + 1];
    if (!next) return;
    setPickerExpandedId(next.id);
    setPickerWeights(prev => {
      if (prev[next.id]) return prev;
      const containerType = next.containerType || 'Bottle';
      const hasWeights    = !!(next.full_weight);
      const defaultWT     = hasWeights ? 'g' : containerType;
      return { ...prev, [next.id]: { weightType: defaultWT, currentWeight: '' } };
    });
    // scroll al siguiente item
    setTimeout(() => {
      const el = document.getElementById(`picker-product-${next.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  // ── PICKER: confirmar selección de UN producto ───────────────────────────
  const handleConfirmSingleProduct = (product) => {
    const pw = pickerWeights[product.id] || {};
    const { table, target } = selectingFor;
    setFormData(prev => {
      const currentList = [...prev[table]];
      const row = buildRowFromProduct(product, 1, pw.weightType || null, pw.currentWeight || '');
      if (target !== null && typeof target === 'object') {
        const { index, position } = target;
        currentList.splice(position === 'above' ? index : index + 1, 0, row);
      } else if (target !== null && typeof target === 'number') {
        currentList[target] = { ...currentList[target], ...row, displayOrder: currentList[target].displayOrder };
      } else {
        currentList.push(row);
      }
      const reordered = currentList.map((r, i) => ({ ...r, displayOrder: i + 1 }));
      return { ...prev, [table]: reordered };
    });
    // limpiar este producto del picker y mover al siguiente
    const list = filteredProducts;
    const idx  = list.findIndex(p => p.id === product.id);
    const next = list[idx + 1];
    setPickerWeights(prev => { const c = { ...prev }; delete c[product.id]; return c; });
    if (next) {
      setPickerExpandedId(next.id);
      setPickerWeights(prev => {
        if (prev[next.id]) return prev;
        const containerType = next.containerType || 'Bottle';
        const hasWeights    = !!(next.full_weight);
        return { ...prev, [next.id]: { weightType: hasWeights ? 'g' : containerType, currentWeight: '' } };
      });
      setTimeout(() => {
        const el = document.getElementById(`picker-product-${next.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    } else {
      // Ya no cerramos el modal automáticamente al agregar el último producto:
      // el usuario decide cuándo cerrarlo (Cancel / X)
      setPickerExpandedId(null);
    }
  };

  // ── PICKER: agregar todos los que tienen peso ─────────────────────────────
  const handleAddAllWithWeight = () => {
    const withWeight = Object.entries(pickerWeights).filter(([, pw]) => pw.currentWeight !== '');
    if (withWeight.length === 0) { alert('Please enter at least one current weight'); return; }
    const { table, target } = selectingFor;
    setFormData(prev => {
      let currentList = [...prev[table]];
      withWeight.forEach(([productId, pw]) => {
        const product = products.find(p => p.id === parseInt(productId));
        if (!product) return;
        const row = buildRowFromProduct(product, 1, pw.weightType || null, pw.currentWeight || '');
        if (target !== null && typeof target === 'object') {
          const { index, position } = target;
          currentList.splice(position === 'above' ? index : index + 1, 0, row);
        } else {
          currentList.push(row);
        }
      });
      const reordered = currentList.map((r, i) => ({ ...r, displayOrder: i + 1 }));
      return { ...prev, [table]: reordered };
    });
    setShowProductModal(false);
    setSelectingFor(null);
    setPickerWeights({});
    setPickerExpandedId(null);
  };

  // ── PICKER PREP: confirmar con cantidad ─────────────────────
  const handleConfirmSinglePrep = (prep) => {
    const qty = pickerPrepQty[prep.id]?.currentQty || '';
    // Construir el row con currentWeight pre-llenado
    const newRow = {
      ...buildRowFromPrep(prep, 1),
      currentWeight: qty,
      wholesaleValue: qty ? calculatePrepWsValue(qty, prep.yield_unit, prep.yield_quantity, prep.yield_unit, prep.total_cost) : ''
    };
    const { target } = selectingFor || { target: null };
    setFormData(prev => {
      const currentList = [...prev.prepItems];
      if (target !== null && typeof target === 'object') {
        const { index, position } = target;
        currentList.splice(position === 'above' ? index : index + 1, 0, newRow);
      } else {
        currentList.push(newRow);
      }
      return { ...prev, prepItems: currentList.map((r, i) => ({ ...r, displayOrder: i + 1 })) };
    });
    // Limpiar y avanzar al siguiente
    const list = filteredPreps;
    const idx  = list.findIndex(p => p.id === prep.id);
    const next = list[idx + 1];
    setPickerPrepQty(prev => { const c = { ...prev }; delete c[prep.id]; return c; });
    if (next) {
      setPickerPrepExpandedId(next.id);
      setPickerPrepQty(prev => prev[next.id] ? prev : { ...prev, [next.id]: { currentQty: '' } });
      setTimeout(() => {
        const el = document.getElementById(`picker-prep-${next.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    } else {
      setPickerPrepExpandedId(null);
    }
  };

  const handlePickerPrepToggle = (prep) => {
    if (pickerPrepExpandedId === prep.id) {
      setPickerPrepExpandedId(null);
    } else {
      setPickerPrepExpandedId(prep.id);
      setPickerPrepQty(prev => prev[prep.id] ? prev : { ...prev, [prep.id]: { currentQty: '' } });
    }
  };

  const handlePickerPrepNext = (prep) => {
    const list = filteredPreps;
    const idx  = list.findIndex(p => p.id === prep.id);
    const next = list[idx + 1];
    if (!next) return;
    setPickerPrepExpandedId(next.id);
    setPickerPrepQty(prev => prev[next.id] ? prev : { ...prev, [next.id]: { currentQty: '' } });
    setTimeout(() => {
      const el = document.getElementById(`picker-prep-${next.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleRowChange = (table, index, field, value) => {
    setFormData(prev => {
      const updatedList = [...prev[table]];
      updatedList[index] = { ...updatedList[index], [field]: value };
      if (field === 'currentWeight' || field === 'weightType') {
        const row = updatedList[index];
        if (field === 'weightType') {
          const isCount      = countTypes.includes(value);
          const hasWeightsR  = !!(row.fullWeight);
          const useContainer = !isCount && hasWeightsR;
          updatedList[index].wholesalePrice = useContainer
            ? (row.wholesalePriceContainer ?? row.wholesalePrice)
            : (row.wholesalePricePerUnit   ?? row.wholesalePrice);
        }
        const currentWeight = field === 'currentWeight' ? value : row.currentWeight;
        const weightType    = field === 'weightType'    ? value : (row.weightType || 'g');
        const activePrice   = updatedList[index].wholesalePrice;
        const fullWeight    = row.fullWeight;
        const emptyWeight   = row.emptyWeight;
        const containerSize = row.product?.container_size;
        const containerUnit = row.product?.container_unit;
        const containerType = row.product?.containerType || row.containerType;
        updatedList[index].weightWarning  = validateWeight(currentWeight, weightType, fullWeight, emptyWeight, containerType);
        const values = calculateValues(currentWeight, weightType, fullWeight, emptyWeight, activePrice, containerSize, containerUnit, containerType);
        updatedList[index].productWeight  = values.productWeight;
        updatedList[index].percentage     = values.percentage;
        updatedList[index].wholesaleValue = values.ws;
      }
      return { ...prev, [table]: updatedList };
    });
  };

  const handlePrepRowChange = (index, field, value) => {
    setFormData(prev => {
      const updatedList = [...prev.prepItems];
      updatedList[index] = { ...updatedList[index], [field]: value };
      if (field === 'currentWeight' || field === 'weightType') {
        const row = updatedList[index];
        updatedList[index].wholesaleValue = calculatePrepWsValue(
          row.currentWeight, row.weightType,
          row.yieldQuantity, row.yieldUnit, row.prepTotalCost
        );
      }
      return { ...prev, prepItems: updatedList };
    });
  };

  const handleMoveRow = (table, index, direction) => {
    setFormData(prev => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      const list = [...prev[table]];
      if (newIndex < 0 || newIndex >= list.length) return prev;
      [list[index], list[newIndex]] = [list[newIndex], list[index]];
      return { ...prev, [table]: list.map((row, i) => ({ ...row, displayOrder: i + 1 })) };
    });
  };

  const handleRemoveRow = (table, index) => {
    setFormData(prev => {
      const reordered = prev[table]
        .filter((_, i) => i !== index)
        .map((row, i) => ({ ...row, displayOrder: i + 1 }));
      return { ...prev, [table]: reordered };
    });
  };

  // ── IMPORT ───────────────────────────────────────────────────────────────
  const handleOpenImportModal = () => {
    if (!selectedStore) { alert('Please select a store first'); return; }
    setImportFile(null); setImportResult(null); setShowImportModal(true);
  };
  const handleCloseImportModal = () => { setShowImportModal(false); setImportFile(null); setImportResult(null); };
  const handleImportFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const valid = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (!valid.includes(file.type)) { alert('Please select a valid Excel file (.xlsx or .xls)'); e.target.value = ''; return; }
    setImportFile(file); setImportResult(null);
  };
  const handleConfirmImport = async () => {
    if (!importFile) { alert('Please select a file'); return; }
    try {
      setImporting(true);
      const result = await importInventoryFromExcel(importFile, selectedStore.id);
      setImportResult(result);
      if (result.success) await fetchInventories();
    } catch (error) { alert('Error importing: ' + (error.response?.data?.message || error.message)); }
    finally { setImporting(false); }
  };

  // ── LOAD LAST INVENTORY ──────────────────────────────────────────────────
  // useEffect: carga la lista de inventarios disponibles cuando cambia la location
  useEffect(() => {
    // Siempre limpiar primero para que los botones queden deshabilitados
    // mientras se carga o si la location no es válida
    setAvailableInventories([]);
    setSelectedInventoryId('');

    if (!formData.locationId || editingId) return;

    // Locations especiales que no tienen inventarios propios — bloquear botones
    const selectedLocation = locations.find(l => l.id === formData.locationId);
    if (selectedLocation) {
      const name = selectedLocation.name.toLowerCase().trim();
      if (name === 'from excel' || name === 'physical inventory') return;
    }

    const load = async () => {
      try {
        setLoadingInventoryList(true);
        const res = await getInventoriesByLocation(formData.locationId);
        if (res.success) {
          setAvailableInventories(res.data);
          if (res.data.length > 0) setSelectedInventoryId(String(res.data[0].id_inventories));
        }
      } catch { /* silencioso */ }
      finally { setLoadingInventoryList(false); }
    };
    load();
  }, [formData.locationId, editingId, locations]);

  // Helper compartido: transforma respuesta del backend en rows del form
  const _applyLoadedInventory = (data) => {
    const preloaded = data.data.map((item, index) => {
      const containerType     = item.containerType || 'Bottle';
      const fullWeight        = item.product_full_weight  ?? item.full_weight  ?? null;
      const emptyWeight       = item.product_empty_weight ?? item.empty_weight ?? null;
      const hasWeights        = !!(fullWeight);
      const initialWeightType = item.quantity_type || (hasWeights ? 'g' : containerType);
      const { priceContainer, pricePerUnit } = resolvePrices(item.wholesalePrice, item.caseSize);
      const isCount           = countTypes.includes(initialWeightType);
      const useContainerPrice = !isCount && hasWeights;
      return {
        product: {
          id:                item.productId,
          name:              item.productName,
          product_code:      item.productCode,
          containerType,
          container_size:    item.containerSize,
          container_unit:    item.containerUnit,
          caseSize:          item.caseSize,
          full_weight:       fullWeight,
          empty_weight:      emptyWeight,
          wholesalePrice:    item.wholesalePrice || 0,
          category_name:     item.category_name     || 'Uncategorized',
          product_type_name: item.product_type_name || 'Other'
        },
        productId:               item.productId,
        productName:             item.productName,
        productCode:             item.productCode,
        containerType,
        weightType:              initialWeightType,
        currentWeight:           '',
        caseSize:                item.caseSize || '',
        fullWeight,
        emptyWeight,
        netWeight:               item.net_weight || null,
        productWeight:           '',
        percentage:              '',
        wholesalePrice:          useContainerPrice ? priceContainer : pricePerUnit,
        wholesalePriceContainer: priceContainer,
        wholesalePricePerUnit:   pricePerUnit,
        wholesaleValue:          '',
        weightWarning:           { type: null, message: '' },
        category_name:           item.category_name     || 'Uncategorized',
        product_type_name:       item.product_type_name || 'Other',
        displayOrder:            index + 1
      };
    });
    const preloadedPreps = (data.prepData || []).map((item, index) => ({
      prep: {
        id:              item.prepId,
        name:            item.prepName,
        yield_quantity:  item.yieldQuantity,
        yield_unit:      item.yieldUnit,
        yield_unit_cost: item.yieldUnitCost,
        total_cost:      item.totalCost
      },
      prepId:        item.prepId,
      prepName:      item.prepName,
      yieldQuantity: item.yieldQuantity,
      yieldUnit:     item.yieldUnit     || 'Each',
      yieldUnitCost: item.yieldUnitCost,
      prepTotalCost: item.totalCost,
      weightType:    item.quantity_type || item.yieldUnit || 'Each',
      currentWeight: '',
      wholesaleValue: '',
      displayOrder:   index + 1
    }));
    setFormData(prev => ({ ...prev, items: preloaded, prepItems: preloadedPreps }));
  };

  // Cargar TODOS los productos del inventario seleccionado
  const handleLoadLastInventory = async () => {
    if (!formData.locationId) return;
    if (!selectedInventoryId) { alert('Please select an inventory to load'); return; }
    if (formData.items.length > 0 || formData.prepItems.length > 0) {
      const confirm = window.confirm('This will replace the current product list. Continue?');
      if (!confirm) return;
    }
    try {
      setLoadingLastInventory(true);
      const data = await getInventoryProducts(selectedInventoryId, false);
      if (!data.success || (data.data.length === 0 && (!data.prepData || data.prepData.length === 0))) {
        alert('No products found in this inventory.'); return;
      }
      _applyLoadedInventory(data);
    } catch { alert('Error loading inventory products.'); }
    finally  { setLoadingLastInventory(false); }
  };

  // Cargar solo los productos con quantity = 0 (no contados)
  const handleLoadUncounted = async () => {
    if (!formData.locationId) return;
    if (!selectedInventoryId) { alert('Please select an inventory first'); return; }
    if (formData.items.length > 0 || formData.prepItems.length > 0) {
      const confirm = window.confirm('This will replace the current product list with uncounted items only. Continue?');
      if (!confirm) return;
    }
    try {
      setLoadingLastInventory(true);
      const data = await getInventoryProducts(selectedInventoryId, true);
      if (!data.success || (data.data.length === 0 && (!data.prepData || data.prepData.length === 0))) {
        alert('No uncounted products found in this inventory. All products were already counted!'); return;
      }
      _applyLoadedInventory(data);
    } catch { alert('Error loading uncounted products.'); }
    finally  { setLoadingLastInventory(false); }
  };

  // Fechas con inventarios Locked de TODA la tienda (catálogo para "Uncounted by date")
  const lockedDates = (() => {
    const map = {};
    inventories.forEach(inv => {
      if ((inv.status || '') !== 'Locked') return;
      const key = inv.inventory_date ? inv.inventory_date.split('T')[0] : null;
      if (!key) return;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1)); // [dateKey, count] más reciente primero
  })();

  // ── Search Product ────────────────────────────────────────────────────────
  // Fechas con inventarios (cualquier status) de la tienda, más reciente primero
  const searchableDates = (() => {
    const map = {};
    inventories.forEach(inv => {
      const key = inv.inventory_date ? inv.inventory_date.split('T')[0] : null;
      if (!key) return;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  })();

  const handleOpenSearchModal = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchPrevDate(null);
    setSearchError('');
    setSearchLoading(false);
    setSearchDate(searchableDates.length > 0 ? searchableDates[0][0] : '');
    setShowSearchModal(true);
  };

  const handleCloseSearchModal = () => {
    searchReqRef.current += 1;   // descarta respuestas en vuelo
    setShowSearchModal(false);
  };

  // Búsqueda con retraso (debounce) al escribir o cambiar la fecha
  useEffect(() => {
    if (!showSearchModal || !selectedStore) return undefined;
    const term = searchQuery.trim();
    const reqId = ++searchReqRef.current;
    if (!searchDate || term.length < 2) {
      setSearchResults(null);
      setSearchLoading(false);
      setSearchError('');
      return undefined;
    }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/inventories/search-product', {
          params: { storeId: selectedStore.id, date: searchDate, q: term }
        });
        if (reqId !== searchReqRef.current) return;
        setSearchResults(res.data?.data || []);
        setSearchPrevDate(res.data?.prev_date || null);
        setSearchError('');
      } catch (err) {
        if (reqId !== searchReqRef.current) return;
        setSearchResults([]);
        setSearchError('Error searching product.');
      } finally {
        if (reqId === searchReqRef.current) setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [showSearchModal, searchDate, searchQuery, selectedStore]);

  // Cargar los productos que NO se contaron en NINGÚN inventario Locked de una fecha
  // (toda la tienda). No depende de la location del formulario.
  const handleLoadUncountedByDate = async () => {
    if (!uncountedDate) { alert('Please select a date'); return; }
    if (formData.items.length > 0 || formData.prepItems.length > 0) {
      const confirm = window.confirm('This will replace the current product list with the uncounted items for that date. Continue?');
      if (!confirm) return;
    }
    try {
      setLoadingLastInventory(true);
      const res  = await api.get('/inventories/uncounted-by-date', {
        params: { storeId: selectedStore.id, date: uncountedDate }
      });
      const data = res.data;
      if (!data.success || ((data.data?.length || 0) === 0 && (!data.prepData || data.prepData.length === 0))) {
        alert('No uncounted products found for that date. Everything was already counted!'); return;
      }
      _applyLoadedInventory(data);
    } catch { alert('Error loading uncounted products by date.'); }
    finally  { setLoadingLastInventory(false); }
  };


  // ── SAVE ─────────────────────────────────────────────────────────────────
  const buildSavePayload = (status) => ({
    storeId:       selectedStore.id,
    locationId:    formData.locationId,
    inventoryDate: formData.inventoryDate,
    status,
    items: formData.items.map((item, index) => ({
      productId:      item.productId,
      productName:    item.productName || item.product.name,
      quantityType:   item.weightType,
      quantity:       parseFloat(item.currentWeight) || 0,
      caseSize:       item.caseSize ? parseInt(item.caseSize) : null,
      fullWeight:     item.fullWeight  ? parseFloat(item.fullWeight)  : null,
      emptyWeight:    item.emptyWeight ? parseFloat(item.emptyWeight) : null,
      wholesaleValue: parseFloat(item.wholesaleValue) || 0,
      displayOrder:   item.displayOrder || index + 1
    })),
    prepItems: formData.prepItems.map((item, index) => ({
      prepId:         item.prepId,
      prepName:       item.prepName,
      quantityType:   item.weightType,
      quantity:       parseFloat(item.currentWeight) || 0,
      wholesaleValue: parseFloat(item.wholesaleValue) || 0,
      displayOrder:   item.displayOrder || index + 1
    })),
    waste: formData.waste.map((w, index) => ({
      productId:      w.productId,
      productName:    w.productName || w.product.name,
      quantityType:   w.weightType,
      quantity:       parseFloat(w.currentWeight) || 0,
      fullWeight:     w.fullWeight  ? parseFloat(w.fullWeight)  : null,
      emptyWeight:    w.emptyWeight ? parseFloat(w.emptyWeight) : null,
      wholesaleValue: parseFloat(w.wholesaleValue) || 0,
      displayOrder:   w.displayOrder || index + 1
    }))
  });

  const handleSave = async () => {
    if (isSavingRef.current)  return; // bloquea taps duplicados (doble-tap en móvil)
    if (!selectedStore)       { alert('Please select a store');    return; }
    if (!formData.locationId) { alert('Please select a location'); return; }
    if (blockNewInventory)    { alert('This location already has an open inventory. Please continue or close it before creating a new one.'); return; }
    isSavingRef.current = true;
    try {
      setLoading(true);
      const wasCreating = !editingId;
      const response = editingId
        ? await updateInventory(editingId, buildSavePayload('Unlocked'))
        : await createInventory(buildSavePayload('Unlocked'));
      if (response.success) {
        // CRÍTICO: al crear un borrador por primera vez, adoptamos el ID que
        // devuelve el backend. Sin esto, editingId sigue en null y cada toque
        // posterior a "Save" vuelve a llamar createInventory → inventarios
        // duplicados (el bug que se veía sobre todo en tablet).
        if (wasCreating && response.data?.id) {
          setEditingId(response.data.id);
        }
        alert(wasCreating ? 'Inventory draft saved successfully' : 'Draft saved successfully');
        await fetchInventories();
      }
    } catch (error) { alert('Error saving draft: ' + (error.response?.data?.message || error.message)); }
    finally { setLoading(false); isSavingRef.current = false; }
  };

  const handleSaveAndClose = async () => {
    if (isSavingRef.current)  return; // bloquea taps duplicados (doble-tap en móvil)
    if (!selectedStore)       { alert('Please select a store');    return; }
    if (!formData.locationId) { alert('Please select a location'); return; }
    if (blockNewInventory)    { alert('This location already has an open inventory. Please continue or close it before creating a new one.'); return; }
    if (formData.items.length === 0 && formData.prepItems.length === 0) {
      alert('Please add at least one product or prep'); return;
    }
    for (const item of formData.items) {
      if (item.currentWeight === '' || item.currentWeight === null || item.currentWeight === undefined) {
        alert(`Please enter a current weight for: ${item.productName}`); return;
      }
      if (!item.wholesalePrice || parseFloat(item.wholesalePrice) <= 0) {
        alert(`Product ${item.productName} does not have a wholesale price configured`); return;
      }
    }
    for (const item of formData.prepItems) {
      if (item.currentWeight === '' || item.currentWeight === null || item.currentWeight === undefined) {
        alert(`Please enter a quantity for prep: ${item.prepName}`); return;
      }
    }
    for (const w of formData.waste) {
      if (!w.currentWeight || parseFloat(w.currentWeight) <= 0) {
        alert(`Please enter a valid current weight for waste: ${w.productName}`); return;
      }
    }
    isSavingRef.current = true;
    try {
      setLoading(true);
      const response = editingId
        ? await updateInventory(editingId, buildSavePayload('Locked'))
        : await createInventory(buildSavePayload('Locked'));
      if (response.success) {
        alert(editingId ? 'Inventory updated successfully' : 'Inventory created successfully');
        await fetchInventories();
        await fetchProducts(); // refresca Last Inv con el inventario que se acaba de cerrar
        handleCloseCreateModal();
      }
    } catch (error) { alert('Error saving inventory: ' + (error.response?.data?.message || error.message)); }
    finally { setLoading(false); isSavingRef.current = false; }
  };

  // ── EDIT ─────────────────────────────────────────────────────────────────
  const handleEditInventory = async (id) => {
    try {
      setLoading(true);
      const response = await getInventoryById(id);
      if (response.success) {
        const inv = response.data;
        if (inv.status === 'Locked') { alert('Cannot edit a locked inventory'); return; }
        const localDate = inv.inventory_date.split('T')[0];
        const mapRow = (item) => {
          const found         = products.find(p => p.id === item.id_product);
          const weightType    = item.quantity_type || 'g';
          const currentWeight = item.quantity || '';
          const fullWeight    = item.product_full_weight  ?? item.full_weight  ?? null;
          const emptyWeight   = item.product_empty_weight ?? item.empty_weight ?? null;
          const rawWholesale  = found?.wholesalePrice ?? item.wholesale_price ?? 0;
          const rawCaseSize   = found?.caseSize       ?? item.case_size       ?? null;
          const { priceContainer, pricePerUnit } = resolvePrices(rawWholesale, rawCaseSize);
          const isCount           = countTypes.includes(weightType);
          const hasWeightsEdit    = !!(fullWeight);
          const useContainerPrice = !isCount && hasWeightsEdit;
          const activePrice       = useContainerPrice ? priceContainer : pricePerUnit;
          const containerSize = item.container_size || null;
          const containerUnit = item.container_unit || null;
          const containerType = item.container_type || null;
          const calc = calculateValues(currentWeight, weightType, fullWeight, emptyWeight, activePrice, containerSize, containerUnit, containerType);
          return {
            product: {
              id:                item.id_product,
              name:              item.product_name,
              product_code:      item.product_code,
              containerType:     item.container_type,
              container_size:    item.container_size || '',
              container_unit:    item.container_unit || '',
              caseSize:          item.case_size,
              full_weight:       item.product_full_weight || item.full_weight,
              empty_weight:      item.product_empty_weight || item.empty_weight,
              wholesalePrice:    rawWholesale,
              category_name:     item.category_name     || 'Uncategorized',
              product_type_name: item.product_type_name || 'Other'
            },
            productId:               item.id_product,
            productName:             item.product_name,
            productCode:             item.product_code,
            containerType:           item.container_type,
            weightType,
            currentWeight,
            caseSize:                item.case_size || '',
            fullWeight,
            emptyWeight,
            netWeight:               item.net_weight || '',
            productWeight:           calc.productWeight,
            percentage:              calc.percentage,
            wholesalePrice:          activePrice,
            wholesalePriceContainer: priceContainer,
            wholesalePricePerUnit:   pricePerUnit,
            wholesaleValue:          calc.ws || item.wholesale_value || '',
            weightWarning:           { type: null, message: '' },
            category_name:           item.category_name     || 'Uncategorized',
            product_type_name:       item.product_type_name || 'Other',
            displayOrder:            item.display_order
          };
        };
        const mapPrepRow = (item) => {
          const ws = calculatePrepWsValue(
            item.quantity, item.quantity_type,
            item.yield_quantity, item.yield_unit, item.prep_total_cost
          );
          return {
            prep: {
              id:              item.id_prep,
              name:            item.prep_name,
              yield_quantity:  item.yield_quantity,
              yield_unit:      item.yield_unit,
              yield_unit_cost: item.yield_unit_cost,
              total_cost:      item.prep_total_cost
            },
            prepId:        item.id_prep,
            prepName:      item.prep_name,
            yieldQuantity: item.yield_quantity,
            yieldUnit:     item.yield_unit     || 'Each',
            yieldUnitCost: item.yield_unit_cost,
            prepTotalCost: item.prep_total_cost,
            weightType:    item.quantity_type  || item.yield_unit || 'Each',
            currentWeight: item.quantity       || '',
            wholesaleValue: ws || item.wholesale_value || '',
            displayOrder:  item.display_order
          };
        };
        setFormData({
          inventoryDate: localDate,
          locationId:    inv.id_location,
          items:         inv.items?.map(mapRow)         || [],
          prepItems:     inv.prepItems?.map(mapPrepRow) || [],
          waste:         inv.losses?.map(mapRow)        || []
        });
        setEditingId(id);
        setTableGroupBy('none');
        setShowCreateModal(true);
      }
    } catch { alert('Error loading inventory'); }
    finally { setLoading(false); }
  };

  // ── AVISO: inventarios abiertos en esta location ─────────────────────────
  // Regla de negocio: una location no puede tener otro inventario abierto
  // (no cerrado) a la vez. Si ya hay uno, el empleado debe continuar/cerrar ese
  // antes de crear uno nuevo. Esto evita los duplicados de la misma fecha.
  // Guardar queda bloqueado mientras existan borradores abiertos aquí.
  const openDraftsForLocation = (!editingId && formData.locationId)
    ? availableInventories.filter(inv => (inv.status || '') !== 'Locked')
    : [];
  const blockNewInventory = openDraftsForLocation.length > 0;

  const renderOpenDraftBanner = () => {
    if (!blockNewInventory) return null;
    const openDrafts = openDraftsForLocation;
    const label = openDrafts.length > 1 ? 'open inventories' : 'an open inventory';
    return (
      <div style={{
        margin: '12px 16px 0', padding: '12px 14px',
        background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#92400e' }}>
          <FaExclamationTriangle /> This location already has {label}
        </div>
        <p style={{ margin: '6px 0 10px', fontSize: 13, color: '#92400e' }}>
          You must continue or close the open inventory before creating a new one for this location.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {openDrafts.map(inv => (
            <div key={inv.id_inventories} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, background: '#fff', border: '1px solid #fcd34d',
              borderRadius: 6, padding: '6px 10px'
            }}>
              <span style={{ fontSize: 13, color: '#78350f' }}>
                {new Date(inv.inventory_date).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: '2-digit' })}
                {' · '}{inv.total_products} products
              </span>
              <button
                type="button"
                onClick={() => handleEditInventory(inv.id_inventories)}
                disabled={loading}
                style={{
                  background: '#d97706', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '6px 12px', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
                }}
              >
                Continue this one
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleOpenDeleteModal  = (id) => { setDeleteId(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setDeleteId(null); };
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteInventory(deleteId);
      if (response.success) { alert('Inventory deleted successfully'); await fetchInventories(); }
      handleCloseDeleteModal();
    } catch (error) { alert('Error deleting inventory: ' + (error.response?.data?.message || error.message)); }
    finally { setLoading(false); }
  };
  const handleToggleLock = async (id) => {
    try {
      setLoading(true);
      const response = await toggleLockInventory(id);
      if (response.success) { alert(response.message); await fetchInventories(); await fetchProducts(); }
    } catch { alert('Error updating inventory status'); }
    finally { setLoading(false); }
  };

  const filteredInventories = inventories.filter(inv => {
    const matchesSearch = searchTerm === '' ||
      (inv.location_name && inv.location_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const invDate     = inv.inventory_date ? inv.inventory_date.split('T')[0] : '';
    const matchesDate = filterDate === '' || invDate === filterDate;
    return matchesSearch && matchesDate;
  });

  // ── Catálogo agrupado por fecha (día) ──
  // filteredInventories ya viene ordenado por fecha DESC desde el backend.
  const inventoriesByDate = (() => {
    const map = {};
    filteredInventories.forEach(inv => {
      const key = inv.inventory_date ? inv.inventory_date.split('T')[0] : 'unknown';
      if (!map[key]) map[key] = [];
      map[key].push(inv);
    });
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1)); // fecha más reciente primero
  })();

  const toggleDate = (dateKey) =>
    setExpandedDates(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));


  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.product_code && p.product_code.toLowerCase().includes(productSearch.toLowerCase()))
  );
  const filteredPreps = preps.filter(p =>
    p.name.toLowerCase().includes(prepSearch.toLowerCase())
  );

  const calculateTotals = () => ({
    totalWS:    formData.items.reduce((sum, item) => sum + (parseFloat(item.wholesaleValue) || 0), 0)
              + formData.prepItems.reduce((sum, item) => sum + (parseFloat(item.wholesaleValue) || 0), 0),
    totalWaste: formData.waste.reduce((sum, w) => sum + (parseFloat(w.wholesaleValue) || 0), 0)
  });
  const totals = calculateTotals();

  // ── EXPORTS ───────────────────────────────────────────────────────────────
  const getCountBy = (container_size, container_unit, container_type) => {
    if (parseFloat(container_size) === 1) return container_unit || '';
    return container_type || '';
  };

  const exportInventoryToCSV = (items, storeName, locationName, date, gBy = 'category') => {
    const headers = ['Category', 'Product Code', 'Product Name', 'Last Inv', 'Purchase', 'Count By', 'INV', 'INV 2', 'INV 3'];
    const rows = [];
    if (gBy === 'type') {
      const grouped = groupByTypeAndCategory(items);
      Object.entries(grouped).forEach(([typeName, categories]) => {
        rows.push([typeName, '', '', '', '', '', '', '', '']);
        Object.entries(categories).forEach(([catName, catItems]) => {
          rows.push(['', catName, '', '', '', '', '', '', '']);
          catItems.forEach(item => rows.push(['', '', item.product_code || '', item.name || item.product_name || '', item.last_inv_quantity ?? 0, '', item.item_type === 'prep' ? item.container_unit || '' : getCountBy(item.container_size, item.container_unit, item.container_type), '', '']));
        });
      });
    } else {
      const grouped = groupByCategory(items);
      Object.entries(grouped).forEach(([catName, catItems]) => {
        rows.push([catName, '', '', '', '', '', '', '', '']);
        catItems.forEach(item => rows.push(['', '', item.product_code || '', item.name || item.product_name || '', item.last_inv_quantity ?? 0, '', item.item_type === 'prep' ? item.container_unit || '' : getCountBy(item.container_size, item.container_unit, item.container_type), '', '']));
      });
    }
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `Inventory_${storeName}_${locationName}_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportInventoryToPDF = (items, storeName, locationName, date, gBy = 'category') => {
    const printWindow = window.open('', '_blank');
    let tableRows = '';
    if (gBy === 'type') {
      const grouped = groupByTypeAndCategory(items);
      tableRows = Object.entries(grouped).map(([typeName, categories]) => `
        <tr class="type-row"><td colspan="8"><strong>${typeName}</strong></td></tr>
        ${Object.entries(categories).map(([catName, catItems]) => `
          <tr class="cat-row"><td colspan="8">&nbsp;&nbsp;&nbsp;${catName}</td></tr>
          ${catItems.map(p => `<tr><td>${p.product_code || '-'}</td><td>${p.name || p.product_name}</td><td>${p.last_inv_quantity ?? 0}</td><td></td><td>${p.item_type === 'prep' ? p.container_unit || '' : getCountBy(p.container_size, p.container_unit, p.container_type)}</td><td></td><td></td><td></td></tr>`).join('')}
        `).join('')}
      `).join('');
    } else {
      const grouped = groupByCategory(items);
      tableRows = Object.entries(grouped).map(([catName, prods]) => `
        <tr class="cat-row"><td colspan="8"><strong>${catName}</strong></td></tr>
        ${prods.map(p => `<tr><td>${p.product_code || '-'}</td><td>${p.name || p.product_name}</td><td>${p.last_inv_quantity ?? 0}</td><td></td><td>${p.item_type === 'prep' ? p.container_unit || '' : getCountBy(p.container_size, p.container_unit, p.container_type)}</td><td></td><td></td><td></td></tr>`).join('')}
      `).join('');
    }
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Inventory - ${storeName} - ${locationName}</title>
      <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h1{font-size:18px;margin-bottom:4px}p{margin:2px 0 16px;color:#555}table{width:100%;border-collapse:collapse}th{background:#0ea5e9;color:white;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #e2e8f0}.type-row td{background:#1e293b;color:#fff;font-weight:bold;padding:8px}.cat-row td{background:#f1f5f9;font-weight:bold;padding:6px 8px}@media print{button{display:none}}</style></head>
      <body><h1>Inventory — ${storeName} — ${locationName}</h1>
      <p>Date: ${date} | Grouped by: ${gBy === 'type' ? 'Product Type → Category' : 'Category'}</p>
      <button onclick="window.print()" style="margin-bottom:16px;padding:8px 16px;background:#0ea5e9;color:white;border:none;border-radius:4px;cursor:pointer;">Print / Save PDF</button>
      <table><thead><tr><th>Code</th><th>Name</th><th>Last Inv</th><th>Purchase</th><th>Count By</th><th>INV</th><th>INV 2</th><th>INV 3</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
  };

  const handlePrintExport = async (format) => {
    if (!printLocationId) { alert('Please select a location'); return; }
    try {
      setPrintExportLoading(true);
      const response = await api.get('/inventories/print-format', {
        params: { storeId: selectedStore.id, locationId: printLocationId }
      });
      if (response.data.success) {
        const location     = locations.find(l => l.id === parseInt(printLocationId));
        const locationName = location?.name || 'Location';
        const date         = new Date().toLocaleDateString('en-CA');
        if (format === 'pdf') exportInventoryToPDF(response.data.data, selectedStore.name, locationName, date, groupBy);
        else                  exportInventoryToCSV(response.data.data, selectedStore.name, locationName, date, groupBy);
        setShowPrintModal(false);
      }
    } catch (err) { alert('Error exporting: ' + err.message); }
    finally { setPrintExportLoading(false); }
  };

  // ── PANEL "PRELOAD PRODUCTS" ─────────────────────────────────────────────
  // Un solo render para desktop y mobile (el CSS decide si va en 2 columnas o apilado).
  // Misma lógica de siempre: handleLoadLastInventory / handleLoadUncounted /
  // handleLoadUncountedByDate, solo cambia cómo se presenta.
  const renderLoadPanel = () => (
    <div className="invent-load-card">
      <div className="invent-load-card-head">
        <span className="invent-load-card-title">Preload products</span>
        <span className="invent-load-card-sub">Start from an earlier count instead of adding products one by one</span>
      </div>

      <div className="invent-load-card-grid">
        {/* ── A: desde un inventario anterior (esta location) ── */}
        <section className="invent-load-section">
          <header className="invent-load-section-head">
            <span className="invent-load-section-icon"><FaHistory /></span>
            <div>
              <h4 className="invent-load-section-title">From a previous inventory</h4>
              <p className="invent-load-section-desc">This location. Load every product, or only the ones still missing a count.</p>
            </div>
          </header>

          {loadingInventoryList ? (
            <span className="invent-load-note">Loading inventories...</span>
          ) : availableInventories.length === 0 ? (
            <span className="invent-load-note">No previous inventories for this location</span>
          ) : (
            <select
              className="invent-load-select"
              value={selectedInventoryId}
              onChange={(e) => setSelectedInventoryId(e.target.value)}
              disabled={loadingLastInventory}
            >
              {availableInventories.map(inv => (
                <option key={inv.id_inventories} value={inv.id_inventories}>
                  {new Date(inv.inventory_date).toLocaleDateString('en-CA', {
                    year: 'numeric', month: 'short', day: '2-digit'
                  })} · {inv.status} · {inv.total_products} products
                </option>
              ))}
            </select>
          )}

          <div className="invent-load-actions">
            <button
              className="invent-btn-load-last"
              onClick={handleLoadLastInventory}
              disabled={loadingLastInventory || !selectedInventoryId}
              type="button"
              title="Load all products from the selected inventory"
            >
              {loadingLastInventory ? 'Loading...' : '⟳ Load all'}
            </button>
            <button
              className="invent-btn-load-uncounted"
              onClick={handleLoadUncounted}
              disabled={loadingLastInventory || !selectedInventoryId}
              type="button"
              title="Load only the products that were not counted in the selected inventory"
            >
              ⚠ Load uncounted
            </button>
          </div>
        </section>

        {/* ── B: no contados por fecha (toda la tienda) ── */}
        <section className="invent-load-section">
          <header className="invent-load-section-head">
            <span className="invent-load-section-icon invent-load-section-icon--amber"><FaCalendarAlt /></span>
            <div>
              <h4 className="invent-load-section-title">Uncounted by date</h4>
              <p className="invent-load-section-desc">Whole store. Products not counted in any locked inventory of that date.</p>
            </div>
          </header>

          <select
            className="invent-load-select"
            value={uncountedDate}
            onChange={(e) => setUncountedDate(e.target.value)}
            disabled={loadingLastInventory || lockedDates.length === 0}
          >
            <option value="">{lockedDates.length === 0 ? 'No locked inventories' : 'Select a date...'}</option>
            {lockedDates.map(([d, count]) => (
              <option key={d} value={d}>
                {new Date(d + 'T00:00:00').toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: '2-digit' })} · {count} inv.
              </option>
            ))}
          </select>

          <div className="invent-load-actions">
            <button
              className="invent-btn-load-uncounted"
              onClick={handleLoadUncountedByDate}
              disabled={loadingLastInventory || !uncountedDate}
              type="button"
              title="Load products not counted in ANY locked inventory of that date"
            >
              ⚠ Load uncounted (by date)
            </button>
          </div>
        </section>
      </div>
    </div>
  );

  // Last Inv de un renglón: viene de /inventories/available-products (día Locked
  // más reciente de la tienda, igual que Physical Inventory), buscado por producto.
  const getRowLastInv = (row) => {
    const found = products.find(p => p.id === row.productId);
    return found?.lastInvQuantity ?? 0;
  };

  // ── RENDER PRODUCT TABLE ROWS ────────────────────────────────────────────
  const renderProductTableRows = (list, table, isWaste) => {
    const renderRow = (row, globalIndex) => (
      <tr key={globalIndex} className={row.weightWarning?.type === 'error' ? 'invent-row-error' : row.weightWarning?.type === 'warning' ? 'invent-row-warning' : ''}>
        <td className="invent-add-cell invent-col-add">
          <div className="invent-order-controls">
            <button className="invent-btn-order invent-btn-add-row" onClick={() => handleOpenProductModal(table, { index: globalIndex, position: 'above' })} disabled={loading} title="Insert above"><FaPlus /></button>
            <span className="invent-order-number"></span>
            <button className="invent-btn-order invent-btn-add-row" onClick={() => handleOpenProductModal(table, { index: globalIndex, position: 'below' })} disabled={loading} title="Insert below"><FaPlus /></button>
          </div>
        </td>
        <td className="invent-order-cell invent-col-order">
          <div className="invent-order-controls">
            <button className="invent-btn-order" onClick={() => handleMoveRow(table, globalIndex, 'up')} disabled={globalIndex === 0 || loading}><FaArrowUp /></button>
            <span className="invent-order-number">{row.displayOrder || globalIndex + 1}</span>
            <button className="invent-btn-order" onClick={() => handleMoveRow(table, globalIndex, 'down')} disabled={globalIndex === list.length - 1 || loading}><FaArrowDown /></button>
          </div>
        </td>
        <td className="invent-col-product">
          <button className="invent-btn-link" onClick={() => handleOpenProductModal(table, globalIndex)}>
            {row.productName || row.product?.name}
          </button>
        </td>
        <td className="invent-code-cell invent-col-code">{row.productCode || row.product?.product_code || '-'}</td>
        <td className="invent-size-cell invent-col-size">{row.product?.container_size} {row.product?.container_unit}</td>
        <td className="invent-lastinv-cell invent-col-lastinv">{getRowLastInv(row)}</td>
        <td className="invent-col-fullwt">{row.fullWeight  || '-'}</td>
        <td className="invent-col-emptywt">{row.emptyWeight || '-'}</td>
        {!isEmployee() && <td className="invent-col-netwt">{row.netWeight || '-'}</td>}
        <td className="invent-col-wtype">
          {(() => {
            const containerType = row.product?.containerType || row.containerType || '';
            const containerUnit = row.product?.container_unit || row.product?.containerUnit || '';
            const volumeUnits = ['ml', 'L', 'Liter', 'Gallon', 'fl oz'];
            // #1: sin Full Weight y sin Empty Weight → solo Container Type (sin opciones de peso)
            const noWeights = !row.fullWeight && !row.emptyWeight;
            const selectValue = noWeights ? containerType : (row.weightType || 'g');
            return (
              <select value={selectValue} onChange={(e) => handleRowChange(table, globalIndex, 'weightType', e.target.value)} disabled={loading} className="invent-table-select">
                {noWeights
                  ? (<option value={containerType}>{containerType}</option>)
                  : (() => {
                      const isCount  = countTypes.includes(containerType);
                      const isVolume = !isCount && (volumeUnits.includes(containerUnit) || volumeUnits.includes(containerType));
                      if (isCount) return (<><option value={containerType}>{containerType} (container)</option><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>);
                      if (isVolume) return (<><option value={containerType}>{containerType} (container)</option><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>);
                      return (<><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>);
                    })()}
              </select>
            );
          })()}
        </td>
        <td className="invent-col-current">
          <input type="number" step="1" value={row.currentWeight || ''} onChange={(e) => handleRowChange(table, globalIndex, 'currentWeight', e.target.value)} disabled={loading} className="invent-table-input invent-required-field" placeholder="0" />
          {row.weightWarning?.type === 'error'   && <div className="invent-weight-warning invent-weight-error"><FaExclamationTriangle /> {row.weightWarning.message}</div>}
          {row.weightWarning?.type === 'warning' && <div className="invent-weight-warning invent-weight-warn"><FaExclamationTriangle /> {row.weightWarning.message}</div>}
        </td>
        {SHOW_WEIGHT_PCT_COLUMNS && !isEmployee() && <td className="invent-col-pwt">{row.productWeight && row.productWeight !== '-' ? `${row.productWeight}g` : '-'}</td>}
        {SHOW_WEIGHT_PCT_COLUMNS && <td className="invent-col-pct">{row.percentage ? `${row.percentage}%` : '-'}</td>}
        <td className="invent-col-unit">${row.wholesalePrice ? parseFloat(row.wholesalePrice).toFixed(2) : '0.00'}</td>
        <td className="invent-col-ws">${row.wholesaleValue ? parseFloat(row.wholesaleValue).toFixed(2) : '0.00'}</td>
        <td className="invent-col-actions"><button className="invent-btn-remove-row" onClick={() => handleRemoveRow(table, globalIndex)} disabled={loading}><FaTimes /></button></td>
      </tr>
    );

    if (tableGroupBy === 'none') return list.map((row, index) => renderRow(row, index));

    // Columnas visibles: 13 fijas (+ Net Wt si no es empleado, + Percentage y Product Wt si están activas)
    const colSpan = 13
      + (isEmployee() ? 0 : 1)
      + (SHOW_WEIGHT_PCT_COLUMNS ? (isEmployee() ? 1 : 2) : 0);

    if (tableGroupBy === 'type') {
      const grouped = groupByTypeAndCategory(list);
      return Object.entries(grouped).map(([typeName, categories]) => (
        <React.Fragment key={typeName}>
          <tr className="invent-type-row"><td colSpan={colSpan}><strong>{typeName}</strong></td></tr>
          {Object.entries(categories).map(([catName, catItems]) => (
            <React.Fragment key={`${typeName}-${catName}`}>
              <tr className="invent-category-row invent-category-sub"><td colSpan={colSpan}>&nbsp;&nbsp;&nbsp;{catName}</td></tr>
              {catItems.map(row => { const globalIndex = list.indexOf(row); return renderRow(row, globalIndex); })}
            </React.Fragment>
          ))}
        </React.Fragment>
      ));
    }

    const grouped = groupByCategory(list);
    return Object.entries(grouped).map(([catName, catItems]) => (
      <React.Fragment key={catName}>
        <tr className="invent-category-row"><td colSpan={colSpan}><strong>{catName}</strong></td></tr>
        {catItems.map(row => { const globalIndex = list.indexOf(row); return renderRow(row, globalIndex); })}
      </React.Fragment>
    ));
  };

  // ── MOBILE PRODUCT CARDS ─────────────────────────────────────────────────
  const renderProductMobileCards = (list, table, isWaste) => {
    if (list.length === 0) return null;
    return list.map((row, index) => {
      const hasWeightWarning = row.weightWarning?.type === 'error' || row.weightWarning?.type === 'warning';
      const containerType = row.product?.containerType || row.containerType || '';
      const containerUnit = row.product?.container_unit || '';
      const volumeUnits   = ['ml', 'L', 'Liter', 'Gallon', 'fl oz'];
      const isCount  = countTypes.includes(containerType);
      const isVolume = !isCount && (volumeUnits.includes(containerUnit) || volumeUnits.includes(containerType));

      return (
        <div
          key={index}
          className={[
            'invent-item-card',
            isWaste ? 'invent-item-card--waste' : '',
            hasWeightWarning ? 'invent-item-card--warn' : ''
          ].filter(Boolean).join(' ')}
        >
          <div className="invent-item-card-header">
            <div className="invent-item-card-title">
              <button className="invent-btn-link" onClick={() => handleOpenProductModal(table, index)}>
                <span className="invent-item-card-name">{row.productName || row.product?.name}</span>
              </button>
              <span className="invent-item-card-meta">
                {row.productCode || row.product?.product_code || '—'}
                {(row.product?.container_size || row.product?.container_unit) &&
                  <> · {row.product?.container_size} {row.product?.container_unit}</>}
              </span>
            </div>
            <div className="invent-item-card-header-right">
              <div className="invent-item-card-order">
                <button className="invent-btn-order" onClick={() => handleMoveRow(table, index, 'up')} disabled={index === 0 || loading}><FaArrowUp /></button>
                <span className="invent-order-number">{row.displayOrder || index + 1}</span>
                <button className="invent-btn-order" onClick={() => handleMoveRow(table, index, 'down')} disabled={index === list.length - 1 || loading}><FaArrowDown /></button>
              </div>
              <button className="invent-btn-remove-row" onClick={() => handleRemoveRow(table, index)} disabled={loading}><FaTimes /></button>
            </div>
          </div>

          {(row.fullWeight || row.emptyWeight) && (
            <div className="invent-item-card-weights">
              {row.fullWeight  && <span className="invent-item-weight-chip">Full: {row.fullWeight}g</span>}
              {row.emptyWeight && <span className="invent-item-weight-chip">Empty: {row.emptyWeight}g</span>}
              {row.netWeight   && <span className="invent-item-weight-chip invent-item-weight-chip--net">Net: {row.netWeight}g</span>}
            </div>
          )}

          <div className="invent-item-card-fields">
            <div className="invent-item-card-field invent-item-card-field--full">
              <span className="invent-item-card-label">Weight Type *</span>
              <select
                value={row.weightType || 'g'}
                onChange={(e) => handleRowChange(table, index, 'weightType', e.target.value)}
                disabled={loading}
                className="invent-item-card-select"
              >
                {isCount  && <><option value={containerType}>{containerType} (container)</option><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>}
                {isVolume && <><option value={containerType}>{containerType} (container)</option><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>}
                {!isCount && !isVolume && <><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>}
              </select>
            </div>

            <div className="invent-item-card-field invent-item-card-field--full">
              <span className="invent-item-card-label">Current Weight *</span>
              <input
                type="number" step="1" placeholder="0"
                value={row.currentWeight || ''}
                onChange={(e) => handleRowChange(table, index, 'currentWeight', e.target.value)}
                disabled={loading}
                className={`invent-item-card-input invent-item-card-input--primary${hasWeightWarning ? ' invent-item-card-input--warn' : ''}`}
              />
              {hasWeightWarning && (
                <span className="invent-item-card-warning">
                  <FaExclamationTriangle /> {row.weightWarning.message}
                </span>
              )}
            </div>

            <div className="invent-item-card-field">
              <span className="invent-item-card-label">Last Inv</span>
              <span className="invent-item-card-value">{getRowLastInv(row)}</span>
            </div>
            {SHOW_WEIGHT_PCT_COLUMNS && (
              <div className="invent-item-card-field">
                <span className="invent-item-card-label">Percentage</span>
                <span className="invent-item-card-value">{row.percentage ? `${row.percentage}%` : '—'}</span>
              </div>
            )}
            <div className="invent-item-card-field">
              <span className="invent-item-card-label">Unit Price</span>
              <span className="invent-item-card-value">${row.wholesalePrice ? parseFloat(row.wholesalePrice).toFixed(2) : '0.00'}</span>
            </div>
            {SHOW_WEIGHT_PCT_COLUMNS && !isEmployee() && (
              <div className="invent-item-card-field">
                <span className="invent-item-card-label">Product Wt</span>
                <span className="invent-item-card-value">{row.productWeight && row.productWeight !== '-' ? `${row.productWeight}g` : '—'}</span>
              </div>
            )}
            <div className="invent-item-card-field">
              <span className="invent-item-card-label">WS Value</span>
              <span className="invent-item-card-value invent-item-card-value--ws">
                ${row.wholesaleValue ? parseFloat(row.wholesaleValue).toFixed(2) : '0.00'}
              </span>
            </div>
          </div>

          <div className="invent-item-card-insert">
            <button className="invent-item-card-insert-btn" onClick={() => handleOpenProductModal(table, { index, position: 'above' })} disabled={loading} title="Insert above">
              <FaPlus /> Insert above
            </button>
            <button className="invent-item-card-insert-btn" onClick={() => handleOpenProductModal(table, { index, position: 'below' })} disabled={loading} title="Insert below">
              <FaPlus /> Insert below
            </button>
          </div>
        </div>
      );
    });
  };

  const renderProductTable = (table, title) => {
    const list    = formData[table];
    const isWaste = table === 'waste';
    const totalValue = isWaste ? totals.totalWaste : formData.items.reduce((sum, item) => sum + (parseFloat(item.wholesaleValue) || 0), 0);
    return (
      <div className={`invent-products-section${isWaste ? ' invent-waste-section' : ''}`}>
        <div className="invent-section-header">
          <h3>{isWaste && <FaExclamationTriangle style={{ color: '#f59e0b', marginRight: 6 }} />}{title} ({list.length})</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {list.length > 0 && !isWaste && (
              <span className="invent-desktop-only" style={{ display: 'inline-flex' }}>
                <GroupByToggle value={tableGroupBy} onChange={setTableGroupBy} showNone={true} />
              </span>
            )}
            <div className="invent-totals-preview">
              <span className="invent-total-label">WS Total:</span>
              <span className={`invent-total-value ${isWaste ? 'invent-waste-value' : 'invent-ws'}`}>${totalValue.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {list.length === 0 ? (
          <div className="invent-empty-products">
            <p>No {title.toLowerCase()} added yet.</p>
            <button className={`invent-btn-add-first${isWaste ? ' invent-btn-add-first-waste' : ''}`} onClick={() => handleOpenProductModal(table, null)} disabled={loading}>
              <FaPlus /> Add {isWaste ? 'waste item' : 'first product'}
            </button>
          </div>
        ) : (
          <>
            <div className="invent-table-products-container invent-desktop-only">
              <table className="invent-products-table invent-products-table--edit">
                <thead>
                  <tr>
                    <th className="invent-col-add">Add</th><th className="invent-col-order">Order</th><th className="invent-col-product">Product</th><th className="invent-col-code">Code</th><th className="invent-col-size">Size</th>
                    <th className="invent-col-lastinv">Last Inv</th>
                    <th className="invent-col-fullwt">Full Wt</th><th className="invent-col-emptywt">Empty Wt</th>
                    {!isEmployee() && <th className="invent-col-netwt">Net Wt</th>}
                    <th className="invent-col-wtype">Weight Type *</th><th className="invent-col-current">Current Weight *</th>
                    {SHOW_WEIGHT_PCT_COLUMNS && !isEmployee() && <th className="invent-col-pwt">Product Wt</th>}
                    {SHOW_WEIGHT_PCT_COLUMNS && <th className="invent-col-pct">Percentage</th>}
                    <th className="invent-col-unit">Unit Price</th><th className="invent-col-ws">WS Value</th><th className="invent-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>{renderProductTableRows(list, table, isWaste)}</tbody>
              </table>
            </div>

            <div className="invent-mobile-cards invent-mobile-only">
              {renderProductMobileCards(list, table, isWaste)}
            </div>

            <div className="invent-mobile-only" style={{ marginTop: 10 }}>
              <button
                className={`invent-btn-add-first${isWaste ? ' invent-btn-add-first-waste' : ''}`}
                style={{ width: '100%', justifyContent: 'center', borderRadius: 10, padding: '10px 0' }}
                onClick={() => handleOpenProductModal(table, null)}
                disabled={loading}
              >
                <FaPlus /> Add {isWaste ? 'waste item' : 'product'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // ── TABLA PREPS ───────────────────────────────────────────────────────────
  const renderPrepTable = () => {
    const list       = formData.prepItems;
    const totalValue = list.reduce((sum, item) => sum + (parseFloat(item.wholesaleValue) || 0), 0);
    return (
      <div className="invent-products-section invent-prep-section">
        <div className="invent-section-header">
          <h3>Pre-Batch ({list.length})</h3>
          <div className="invent-totals-preview">
            <span className="invent-total-label">WS Total:</span>
            <span className="invent-total-value invent-ws">${totalValue.toFixed(2)}</span>
          </div>
        </div>
        {list.length === 0 ? (
          <div className="invent-empty-products">
            <p>No pre-batch items added yet.</p>
            <button className="invent-btn-add-first" onClick={() => { setSelectingFor({ table: 'prepItems', target: null }); setPickerTab('preps'); setPrepSearch(''); setProductSearch(''); setPickerWeights({}); setPickerExpandedId(null); setShowProductModal(true); }} disabled={loading}><FaPlus /> Add Pre-Batch</button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8 }} className="invent-desktop-only">
              <button className="invent-btn-add-ingredient" onClick={() => { setSelectingFor({ table: 'prepItems', target: null }); setPickerTab('preps'); setPrepSearch(''); setProductSearch(''); setPickerWeights({}); setPickerExpandedId(null); setShowProductModal(true); }} disabled={loading} type="button"><FaPlus /> Add Pre-Batch</button>
            </div>
            <div className="invent-table-products-container invent-desktop-only">
              <table className="invent-products-table invent-products-table--prep">
                <thead>
                  <tr>
                    <th className="invent-pcol-order">Order</th>
                    <th className="invent-pcol-name">Prep Name</th>
                    <th className="invent-pcol-yield">Yield</th>
                    <th className="invent-pcol-cost">Unit Cost</th>
                    <th className="invent-pcol-unit">Unit *</th>
                    <th className="invent-pcol-qty">Current Qty *</th>
                    <th className="invent-pcol-ws">WS Value</th>
                    <th className="invent-pcol-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row, index) => (
                    <tr key={index}>
                      <td className="invent-add-cell invent-pcol-order">
                        <div className="invent-order-controls">
                          <button className="invent-btn-order invent-btn-add-row" onClick={() => { setPickerTab('preps'); setProductSearch(''); setPrepSearch(''); setPickerWeights({}); setPickerExpandedId(null); setSelectingFor({ table: 'prepItems', target: { index, position: 'above' } }); setShowProductModal(true); }} disabled={loading} title="Insert above"><FaPlus /></button>
                          <span className="invent-order-number"></span>
                          <button className="invent-btn-order invent-btn-add-row" onClick={() => { setPickerTab('preps'); setProductSearch(''); setPrepSearch(''); setPickerWeights({}); setPickerExpandedId(null); setSelectingFor({ table: 'prepItems', target: { index, position: 'below' } }); setShowProductModal(true); }} disabled={loading} title="Insert below"><FaPlus /></button>
                        </div>
                      </td>
                      <td className="invent-pcol-name"><strong>{row.prepName}</strong></td>
                      <td className="invent-size-cell invent-pcol-yield">{row.yieldQuantity} {row.yieldUnit}</td>
                      <td className="invent-pcol-cost">${row.yieldUnitCost ? parseFloat(row.yieldUnitCost).toFixed(4) : '0.0000'} / {row.yieldUnit}</td>
                      <td className="invent-pcol-unit">
                        <select value={row.weightType || row.yieldUnit || 'Each'} onChange={(e) => handlePrepRowChange(index, 'weightType', e.target.value)} disabled={loading} className="invent-table-select">
                          {['ml', 'L', 'Liter', 'Gallon', 'fl oz'].includes(row.yieldUnit) && (<><option value="ml">ml</option><option value="L">L (liter)</option><option value="Gallon">Gallon</option><option value="fl oz">fl oz</option></>)}
                          {['g', 'kg', 'oz', 'lb'].includes(row.yieldUnit) && (<><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>)}
                          {!['ml','L','Liter','Gallon','fl oz','g','kg','oz','lb'].includes(row.yieldUnit) && (<option value="Each">Each</option>)}
                        </select>
                      </td>
                      <td className="invent-pcol-qty"><input type="number" step="1" min="0" value={row.currentWeight || ''} onChange={(e) => handlePrepRowChange(index, 'currentWeight', e.target.value)} disabled={loading} className="invent-table-input invent-required-field" placeholder="0" /></td>
                      <td className="invent-pcol-ws">${row.wholesaleValue ? parseFloat(row.wholesaleValue).toFixed(2) : '0.00'}</td>
                      <td className="invent-pcol-actions"><button className="invent-btn-remove-row" onClick={() => handleRemoveRow('prepItems', index)} disabled={loading}><FaTimes /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="invent-mobile-cards invent-mobile-only">
              {list.map((row, index) => (
                <div key={index} className="invent-item-card invent-item-card--prep">
                  <div className="invent-item-card-header">
                    <div className="invent-item-card-title">
                      <span className="invent-item-card-name">{row.prepName}</span>
                      <span className="invent-item-card-meta">
                        Yield: {row.yieldQuantity} {row.yieldUnit} · ${row.yieldUnitCost ? parseFloat(row.yieldUnitCost).toFixed(4) : '0.0000'}/{row.yieldUnit}
                      </span>
                    </div>
                    <div className="invent-item-card-header-right">
                      <div className="invent-item-card-order">
                        <button className="invent-btn-order" onClick={() => handleMoveRow('prepItems', index, 'up')} disabled={index === 0 || loading}><FaArrowUp /></button>
                        <span className="invent-order-number">{row.displayOrder || index + 1}</span>
                        <button className="invent-btn-order" onClick={() => handleMoveRow('prepItems', index, 'down')} disabled={index === list.length - 1 || loading}><FaArrowDown /></button>
                      </div>
                      <button className="invent-btn-remove-row" onClick={() => handleRemoveRow('prepItems', index)} disabled={loading}><FaTimes /></button>
                    </div>
                  </div>
                  <div className="invent-item-card-fields">
                    <div className="invent-item-card-field invent-item-card-field--full">
                      <span className="invent-item-card-label">Unit *</span>
                      <select
                        value={row.weightType || row.yieldUnit || 'Each'}
                        onChange={(e) => handlePrepRowChange(index, 'weightType', e.target.value)}
                        disabled={loading}
                        className="invent-item-card-select"
                      >
                        {['ml', 'L', 'Liter', 'Gallon', 'fl oz'].includes(row.yieldUnit) && (<><option value="ml">ml</option><option value="L">L (liter)</option><option value="Gallon">Gallon</option><option value="fl oz">fl oz</option></>)}
                        {['g', 'kg', 'oz', 'lb'].includes(row.yieldUnit) && (<><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>)}
                        {!['ml','L','Liter','Gallon','fl oz','g','kg','oz','lb'].includes(row.yieldUnit) && (<option value="Each">Each</option>)}
                      </select>
                    </div>
                    <div className="invent-item-card-field invent-item-card-field--full">
                      <span className="invent-item-card-label">Current Qty *</span>
                      <input
                        type="number" step="1" min="0" placeholder="0"
                        value={row.currentWeight || ''}
                        onChange={(e) => handlePrepRowChange(index, 'currentWeight', e.target.value)}
                        disabled={loading}
                        className="invent-item-card-input invent-item-card-input--primary"
                      />
                    </div>
                    <div className="invent-item-card-field invent-item-card-field--full">
                      <span className="invent-item-card-label">WS Value</span>
                      <span className="invent-item-card-value invent-item-card-value--ws">
                        ${row.wholesaleValue ? parseFloat(row.wholesaleValue).toFixed(2) : '0.00'}
                      </span>
                    </div>
                  </div>
                  <div className="invent-item-card-insert">
                    <button className="invent-item-card-insert-btn" onClick={() => { setPickerTab('preps'); setProductSearch(''); setPrepSearch(''); setPickerWeights({}); setPickerExpandedId(null); setSelectingFor({ table: 'prepItems', target: { index, position: 'above' } }); setShowProductModal(true); }} disabled={loading}><FaPlus /> Insert above</button>
                    <button className="invent-item-card-insert-btn" onClick={() => { setPickerTab('preps'); setProductSearch(''); setPrepSearch(''); setPickerWeights({}); setPickerExpandedId(null); setSelectingFor({ table: 'prepItems', target: { index, position: 'below' } }); setShowProductModal(true); }} disabled={loading}><FaPlus /> Insert below</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="invent-mobile-only" style={{ marginTop: 10 }}>
              <button
                className="invent-btn-add-first"
                style={{ width: '100%', justifyContent: 'center', borderRadius: 10, padding: '10px 0' }}
                onClick={() => { setSelectingFor({ table: 'prepItems', target: null }); setPickerTab('preps'); setPrepSearch(''); setProductSearch(''); setPickerWeights({}); setPickerExpandedId(null); setShowProductModal(true); }}
                disabled={loading}
              >
                <FaPlus /> Add Pre-Batch
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // ── RENDER VIEW MODAL ITEMS ───────────────────────────────────────────────
  const renderViewItems = (items) => {
    const renderItemRow = (item, i) => (
      <tr key={i}>
        <td>{item.product_name}</td>
        <td>{item.product_code || '-'}</td>
        <td>{item.quantity_type}</td>
        <td><strong>{item.quantity}</strong></td>
        <td>${parseFloat(item.wholesale_price || 0).toFixed(2)}</td>
        <td>${parseFloat(item.wholesale_value || 0).toFixed(2)}</td>
      </tr>
    );
    if (groupBy === 'type') {
      const grouped = groupByTypeAndCategory(items);
      return Object.entries(grouped).map(([typeName, categories]) => (
        <React.Fragment key={typeName}>
          <tr className="invent-type-row"><td colSpan={6}><strong>{typeName}</strong></td></tr>
          {Object.entries(categories).map(([catName, catItems]) => (
            <React.Fragment key={`${typeName}-${catName}`}>
              <tr className="invent-category-row invent-category-sub"><td colSpan={6}>&nbsp;&nbsp;&nbsp;{catName}</td></tr>
              {catItems.map((item, i) => renderItemRow(item, `${typeName}-${catName}-${i}`))}
            </React.Fragment>
          ))}
        </React.Fragment>
      ));
    }
    const grouped = groupByCategory(items);
    return Object.entries(grouped).map(([catName, catItems]) => (
      <React.Fragment key={catName}>
        <tr className="invent-category-row"><td colSpan={6}><strong>{catName}</strong></td></tr>
        {catItems.map((item, i) => renderItemRow(item, `${catName}-${i}`))}
      </React.Fragment>
    ));
  };

  if (!selectedStore) {
    return (
      <div className="invent-page">
        <div className="invent-page-header"><h1>Inventories</h1></div>
        <div className="invent-no-store-message"><p>Please select a store from the sidebar to view inventories</p></div>
      </div>
    );
  }

  // Cuántos productos tienen peso llenado en el picker
  const pickerWithWeightCount = Object.values(pickerWeights).filter(pw => pw.currentWeight !== '').length;

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="invent-page">

      {/* ══ PAGE HEADER ══════════════════════════════════════════════════════ */}
      <div className="invent-page-header invent-sticky-header">
        <div className="invent-header-left">
          <h1>Inventories</h1>
          <span className="invent-store-badge">{selectedStore.name}</span>
        </div>

        <div className="invent-header-buttons invent-desktop-only">
          <button className="invent-btn-secondary" onClick={() => { setPrintLocationId(''); setGroupBy('category'); setShowPrintModal(true); }} disabled={loading}>
            <FaDownload /> Print Inventory
          </button>
          <button className="invent-btn-import-excel" onClick={handleOpenImportModal} disabled={loading}>
            <FaFileExcel /> Import from Excel
          </button>
          <button className="invent-btn-search-product" onClick={handleOpenSearchModal} disabled={loading}>
            <FaSearch /> Search Product
          </button>
          <button className="invent-btn-primary" onClick={handleOpenCreateModal} disabled={loading}>
            <FaPlus /> New inventory
          </button>
        </div>

        <div className="invent-mobile-header-right invent-mobile-only" ref={mobileMenuRef}>
          <button className="invent-btn-primary invent-btn-new-mobile" onClick={handleOpenCreateModal} disabled={loading}>
            <FaPlus /> New
          </button>
          <button className="invent-btn-overflow" onClick={() => setShowMobileMenu(v => !v)} aria-label="More options">
            <FaEllipsisV />
          </button>

          {showMobileMenu && (
            <div className="invent-overflow-menu">
              <button className="invent-overflow-item" onClick={() => { setShowMobileMenu(false); setPrintLocationId(''); setGroupBy('category'); setShowPrintModal(true); }}>
                <FaDownload /> Print Inventory
              </button>
              <button className="invent-overflow-item" onClick={() => { setShowMobileMenu(false); handleOpenImportModal(); }}>
                <FaFileExcel /> Import from Excel
              </button>
              <button className="invent-overflow-item" onClick={() => { setShowMobileMenu(false); handleOpenSearchModal(); }}>
                <FaSearch /> Search Product
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="invent-error-message">
          {error}
          <button onClick={fetchInventories}>Retry</button>
        </div>
      )}

      {/* SEARCH + DATE FILTER */}
      <div className="invent-search-section">
        <div className="invent-search-bar">
          <input type="text" placeholder="Search by location..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="invent-date-filter-bar">
          <label className="invent-date-filter-label">Filter by date:</label>
          <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="invent-date-filter-input" />
          {filterDate && (
            <button className="invent-date-filter-clear" onClick={() => setFilterDate('')} title="Clear date filter">
              <FaTimes /> Clear
            </button>
          )}
          {filterDate && (
            <span className="invent-date-filter-count">
              {filteredInventories.length} result{filteredInventories.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* ══ MAIN TABLE — desktop ══════════════════════════════════════════════ */}
      <div className="invent-table-container invent-desktop-only">
        {loading && <div className="invent-loading">Loading...</div>}
        <table className="invent-data-table">
          <thead>
            <tr>
              <th>Location</th><th>Date</th><th>No. Products</th><th>Status</th>
              <th>WS Value</th><th>Waste</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventories.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>{loading ? 'Loading...' : 'No inventories found'}</td></tr>
            ) : inventoriesByDate.map(([dateKey, invs]) => {
              const isOpen = !!expandedDates[dateKey];
              return (
                <React.Fragment key={dateKey}>
                  <tr className="invent-date-group-row" onClick={() => toggleDate(dateKey)} style={{ cursor: 'pointer', background: '#f1f5f9' }}>
                    <td colSpan="7" style={{ padding: '12px 14px', fontWeight: 600, color: '#334155' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        {isOpen ? <FaChevronDown style={{ color: '#0ea5e9' }} /> : <FaChevronRight style={{ color: '#0ea5e9' }} />}
                        <span>{formatDateOnlyCA(dateKey)}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', background: '#e0f2fe', padding: '2px 10px', borderRadius: 999 }}>
                          {invs.length} inventor{invs.length !== 1 ? 'ies' : 'y'}
                        </span>
                      </span>
                    </td>
                  </tr>
                  {isOpen && invs.map((inv) => (
                    <tr key={inv.id_inventories}>
                      <td>{inv.location_name || 'N/A'}</td>
                      <td>{formatDateTimeCA(inv.inventory_date)}</td>
                      <td>{inv.total_products}</td>
                      <td>
                        <span className={`invent-status-badge invent-${inv.status.toLowerCase()}`}>
                          {inv.status === 'Locked' ? <FaLock /> : <FaLockOpen />}{inv.status}
                        </span>
                      </td>
                      <td className="invent-amount-cell">${parseFloat(inv.total_ws_value || 0).toFixed(2)}</td>
                      <td className="invent-amount-cell invent-waste-cell">
                        {inv.total_waste > 0 && <FaExclamationTriangle />}
                        ${parseFloat(inv.total_waste_value || 0).toFixed(2)}
                      </td>
                      <td>
                        <div className="invent-action-buttons">
                          <button className="invent-btn-action invent-btn-lock" onClick={() => handleToggleLock(inv.id_inventories)} disabled={loading} title={inv.status === 'Locked' ? 'Unlock' : 'Lock'}>
                            {inv.status === 'Locked' ? <FaLockOpen /> : <FaLock />}
                          </button>
                          {inv.status === 'Locked' && (
                            <button className="invent-btn-action" onClick={() => handleViewInventory(inv.id_inventories)} disabled={loading} title="View"><FaEye /></button>
                          )}
                          {inv.status === 'Unlocked' && (
                            <>
                              <button className="invent-btn-action" onClick={() => handleEditInventory(inv.id_inventories)} disabled={loading}><FaEdit /></button>
                              <button className="invent-btn-action invent-btn-delete" onClick={() => handleOpenDeleteModal(inv.id_inventories)} disabled={loading}><FaTrash /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ══ MAIN LIST — mobile cards ══════════════════════════════════════════ */}
      <div className="invent-list-cards invent-mobile-only">
        {loading && <div className="invent-loading">Loading...</div>}
        {!loading && filteredInventories.length === 0 && (
          <div className="invent-empty" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
            No inventories found
          </div>
        )}
        {inventoriesByDate.map(([dateKey, invs]) => {
          const isOpen = !!expandedDates[dateKey];
          return (
            <div key={dateKey} className="invent-date-group-mobile" style={{ marginBottom: 12 }}>
              <div
                onClick={() => toggleDate(dateKey)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#f1f5f9', borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: '#334155', marginBottom: isOpen ? 10 : 0 }}
              >
                {isOpen ? <FaChevronDown style={{ color: '#0ea5e9' }} /> : <FaChevronRight style={{ color: '#0ea5e9' }} />}
                <span>{formatDateOnlyCA(dateKey)}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#0369a1', background: '#e0f2fe', padding: '2px 10px', borderRadius: 999 }}>
                  {invs.length} inventor{invs.length !== 1 ? 'ies' : 'y'}
                </span>
              </div>
              {isOpen && invs.map((inv) => (
                <div key={inv.id_inventories} className="invent-list-card">
                  <div className="invent-list-card-top">
                    <div className="invent-list-card-location">{inv.location_name || 'N/A'}</div>
                    <span className={`invent-status-badge invent-${inv.status.toLowerCase()}`}>
                      {inv.status === 'Locked' ? <FaLock /> : <FaLockOpen />}{inv.status}
                    </span>
                  </div>
                  <div className="invent-list-card-date">{formatDateTimeCA(inv.inventory_date)}</div>
                  <div className="invent-list-card-stats">
                    <div className="invent-list-card-stat">
                      <span className="invent-list-card-stat-label">Products</span>
                      <span className="invent-list-card-stat-value">{inv.total_products}</span>
                    </div>
                    <div className="invent-list-card-stat">
                      <span className="invent-list-card-stat-label">WS Value</span>
                      <span className="invent-list-card-stat-value invent-list-card-stat-ws">
                        ${parseFloat(inv.total_ws_value || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="invent-list-card-stat">
                      <span className="invent-list-card-stat-label">Waste</span>
                      <span className={`invent-list-card-stat-value ${parseFloat(inv.total_waste_value || 0) > 0 ? 'invent-list-card-stat-waste' : ''}`}>
                        {parseFloat(inv.total_waste_value || 0) > 0 && <FaExclamationTriangle style={{ fontSize: 10, marginRight: 3 }} />}
                        ${parseFloat(inv.total_waste_value || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="invent-list-card-footer">
                    <button className="invent-list-card-action-btn invent-list-card-action-btn--lock" onClick={() => handleToggleLock(inv.id_inventories)} disabled={loading} title={inv.status === 'Locked' ? 'Unlock' : 'Lock'}>
                      {inv.status === 'Locked' ? <><FaLockOpen /> Unlock</> : <><FaLock /> Lock</>}
                    </button>
                    {inv.status === 'Locked' && (
                      <button className="invent-list-card-action-btn invent-list-card-action-btn--view" onClick={() => handleViewInventory(inv.id_inventories)} disabled={loading}>
                        <FaEye /> View
                      </button>
                    )}
                    {inv.status === 'Unlocked' && (
                      <>
                        <button className="invent-list-card-action-btn invent-list-card-action-btn--edit" onClick={() => handleEditInventory(inv.id_inventories)} disabled={loading}>
                          <FaEdit /> Edit
                        </button>
                        <button className="invent-list-card-action-btn invent-list-card-action-btn--delete" onClick={() => handleOpenDeleteModal(inv.id_inventories)} disabled={loading}>
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ══ MODAL VIEW — READ ONLY ══════════════════════════════════════════ */}
      {showViewModal && viewInventory && (
        <div className="invent-modal-overlay">
          <div className="invent-modal-content invent-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="invent-modal-header">
              <h2>{viewInventory.location_name} — {formatDateTimeCA(viewInventory.inventory_date)}</h2>
              <button className="invent-btn-close" onClick={() => setShowViewModal(false)}><FaTimes /></button>
            </div>
            <div className="invent-modal-body">
              <div className="invent-modal-top-row">
                <GroupByToggle value={groupBy} onChange={setGroupBy} />
              </div>
              <div className="invent-products-section">
                <div className="invent-section-header">
                  <h3>Products ({viewInventory.items?.length || 0})</h3>
                  <div className="invent-totals-preview">
                    <span className="invent-total-label">WS Total:</span>
                    <span className="invent-total-value invent-ws">${parseFloat(viewInventory.total_ws_value || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="invent-table-products-container invent-view-table-container">
                  <table className="invent-products-table invent-modal-view-table">
                    <thead><tr><th>Product</th><th>Code</th><th>Type</th><th>Quantity</th><th>Unit Price</th><th>WS Value</th></tr></thead>
                    <tbody>
                      {!viewInventory.items?.length ? (
                        <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No products</td></tr>
                      ) : renderViewItems(viewInventory.items)}
                    </tbody>
                  </table>
                </div>
              </div>

              {viewInventory.prepItems?.length > 0 && (
                <div className="invent-products-section invent-prep-section" style={{ marginTop: 24 }}>
                  <div className="invent-section-header"><h3>Pre-Batch ({viewInventory.prepItems.length})</h3></div>
                  <div className="invent-table-products-container invent-view-table-container">
                    <table className="invent-products-table invent-modal-view-table">
                      <thead><tr><th>Prep Name</th><th>Yield</th><th>Quantity</th><th>Unit</th><th>WS Value</th></tr></thead>
                      <tbody>
                        {viewInventory.prepItems.map((item, i) => (
                          <tr key={i}>
                            <td>{item.prep_name}</td>
                            <td>{item.yield_quantity} {item.yield_unit}</td>
                            <td><strong>{item.quantity}</strong></td>
                            <td>{item.quantity_type}</td>
                            <td>${parseFloat(item.wholesale_value || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {viewInventory.losses?.length > 0 && (
                <div className="invent-products-section invent-waste-section" style={{ marginTop: 24 }}>
                  <div className="invent-section-header">
                    <h3><FaExclamationTriangle style={{ color: '#f59e0b', marginRight: 6 }} />Waste ({viewInventory.losses.length})</h3>
                    <div className="invent-totals-preview">
                      <span className="invent-total-label">Waste Total:</span>
                      <span className="invent-total-value invent-waste-value">
                        ${viewInventory.losses.reduce((s, w) => s + parseFloat(w.loss_value || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="invent-table-products-container invent-view-table-container">
                    <table className="invent-products-table invent-modal-view-table">
                      <thead><tr><th>Product</th><th>Quantity</th><th>Unit</th><th>WS Value</th></tr></thead>
                      <tbody>
                        {viewInventory.losses.map((w, i) => (
                          <tr key={i}><td>{w.product_name}</td><td><strong>{w.quantity}</strong></td><td>{w.unit}</td><td>${parseFloat(w.loss_value || 0).toFixed(2)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="invent-modal-footer">
              <div className="invent-footer-totals">
                <div className="invent-total-item">
                  <span className="label">WS Total:</span>
                  <span className="value ws">${parseFloat(viewInventory.total_ws_value || 0).toFixed(2)}</span>
                </div>
              </div>
              <div className="invent-footer-actions">
                <button className="invent-btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL SEARCH PRODUCT ════════════════════════════════════════════ */}
      {showSearchModal && (
        <div className="invent-modal-overlay">
          <div className="invent-modal-content invent-modal-search" onClick={(e) => e.stopPropagation()}>
            <div className="invent-modal-header">
              <h2>Search Product</h2>
              <button className="invent-btn-close" onClick={handleCloseSearchModal}><FaTimes /></button>
            </div>

            <div className="invent-modal-body">
              <div className="invent-sp-controls">
                <div className="invent-sp-field invent-sp-field-date">
                  <label>Inventory date</label>
                  <select
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    disabled={searchableDates.length === 0}
                    className="invent-load-select"
                  >
                    {searchableDates.length === 0 && <option value="">No inventories</option>}
                    {searchableDates.map(([d, count]) => (
                      <option key={d} value={d}>{formatDateOnlyCA(d)} ({count})</option>
                    ))}
                  </select>
                </div>
                <div className="invent-sp-field invent-sp-field-search">
                  <label>Product</label>
                  <div className="invent-sp-search-box">
                    <FaSearch className="invent-sp-search-icon" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Name or code (min. 2 letters)"
                      autoFocus
                    />
                    {searchQuery && (
                      <button type="button" className="invent-sp-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear">
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {searchDate && (
                <p className="invent-sp-hint">
                  Last Inv = last <strong>Locked</strong> inventory before this date
                  {searchPrevDate ? <> (<strong>{formatDateOnlyCA(searchPrevDate)}</strong>)</> : null}.
                </p>
              )}

              {searchLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}><div className="invent-loading-spinner" /></div>
              )}

              {!searchLoading && searchError && <div className="invent-sp-empty invent-sp-error">{searchError}</div>}

              {!searchLoading && !searchError && searchResults === null && (
                <div className="invent-sp-empty">Pick a date and type a product name or code.</div>
              )}

              {!searchLoading && !searchError && searchResults !== null && searchResults.length === 0 && (
                <div className="invent-sp-empty">No results for this product on that date.</div>
              )}

              {!searchLoading && !searchError && searchResults && searchResults.map((prod) => {
                const totals = prod.locations.length > 1 ? computeSearchTotals(prod.locations) : [];
                return (
                  <div key={prod.id_product} className="invent-sp-product">
                    <div className="invent-sp-product-head">
                      <span className="invent-sp-product-name">{prod.product_name}</span>
                      <span className="invent-sp-product-code">{prod.product_code || '-'}</span>
                    </div>
                    <table className="invent-sp-table">
                      <thead>
                        <tr>
                          <th className="invent-sp-col-loc">Location</th>
                          <th className="invent-sp-col-type">Weight type</th>
                          <th className="invent-sp-col-num">Last Inv</th>
                          <th className="invent-sp-col-num">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prod.locations.map((l, i) => (
                          <tr key={`${l.id_location ?? 'none'}-${i}`}>
                            <td className="invent-sp-col-loc">{l.location_name}</td>
                            <td className="invent-sp-col-type">{l.weight_type || l.last_weight_type || '—'}</td>
                            <td className="invent-sp-col-num">
                              {fmtSearchQty(l.last_quantity)}
                              {l.last_quantity !== null && l.last_weight_type && l.weight_type && l.last_weight_type !== l.weight_type && (
                                <span className="invent-sp-unit-note"> {l.last_weight_type}</span>
                              )}
                            </td>
                            <td className="invent-sp-col-num">{fmtSearchQty(l.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {totals.length > 0 && (
                        <tfoot>
                          {totals.map(t => (
                            <tr key={t.unit} className="invent-sp-total-row">
                              <td className="invent-sp-col-loc">Total</td>
                              <td className="invent-sp-col-type">{t.unit}</td>
                              <td className="invent-sp-col-num">{fmtSearchQty(t.last)}</td>
                              <td className="invent-sp-col-num">{fmtSearchQty(t.qty)}</td>
                            </tr>
                          ))}
                        </tfoot>
                      )}
                    </table>
                  </div>
                );
              })}
            </div>

            <div className="invent-modal-footer">
              <span />
              <button className="invent-btn-secondary" onClick={handleCloseSearchModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL IMPORT ════════════════════════════════════════════════════ */}
      {showImportModal && (
        <div className="invent-modal-overlay">
          <div className="invent-modal-content invent-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="invent-modal-header">
              <h2>Import Inventory from Excel</h2>
              <button className="invent-btn-close" onClick={handleCloseImportModal}><FaTimes /></button>
            </div>
            <div className="invent-modal-body">
              <div className="invent-import-location-note">
                <FaFileExcel style={{ color: '#10b981', marginRight: 8 }} />
                The inventory will be created with location <strong>"From Excel"</strong> automatically.
              </div>
              <div className="invent-form-group">
                <label>Select Excel File *</label>
                <input type="file" accept=".xlsx,.xls" onChange={handleImportFileSelect} disabled={importing} className="invent-file-input" />
                {importFile && <p style={{ marginTop: 8, fontSize: 13, color: '#64748b' }}>Selected: {importFile.name}</p>}
              </div>
              {importing && <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}><div className="invent-loading-spinner" /></div>}
              {importResult && (
                <div className={`invent-import-result ${importResult.success ? 'invent-success' : 'invent-error'}`}>
                  <p><strong>Import completed!</strong></p>
                  <p>✅ Products imported: {importResult.stats?.importedProducts ?? importResult.stats?.imported}</p>
                  <p>🧪 Pre-batches imported: {importResult.stats?.importedPreps ?? 0}</p>
                  <p>⏭ Skipped: {importResult.stats?.skipped}</p>
                  {importResult.stats?.errors && (
                    <div className="invent-import-errors-list">
                      <p><strong>Errors:</strong></p>
                      {importResult.stats.errors.map((err, i) => <p key={i} className="invent-import-error-item">• {err}</p>)}
                    </div>
                  )}
                </div>
              )}
              <div className="invent-form-hint">
                <strong>Columns (case-insensitive):</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 12 }}>
                  <li><code>Product Name</code> or <code>Name</code> — product/prep name</li>
                  <li><code>Product Code</code> or <code>Code</code> — optional for products</li>
                  <li><code>Closing Inv</code> or <code>Quantity</code> — amount</li>
                  <li><code>Container Type</code> — optional unit override</li>
                  <li><code>Type</code> — write <strong>prep</strong> to import as Pre-Batch</li>
                </ul>
              </div>
            </div>
            <div className="invent-modal-footer">
              <button className="invent-btn-secondary" onClick={handleCloseImportModal} disabled={importing}>{importResult ? 'Close' : 'Cancel'}</button>
              {!importResult && (
                <button className="invent-btn-primary" onClick={handleConfirmImport} disabled={importing || !importFile}>
                  {importing ? 'Importing...' : 'Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL CREATE/EDIT ════════════════════════════════════════════════ */}
      {showCreateModal && (
        <div className="invent-modal-overlay">
          <div className="invent-modal-content invent-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="invent-modal-header">
              <div className="invent-modal-header-inner">
                <h2>{editingId ? 'Edit inventory' : 'New inventory'}</h2>
                {selectedStore && <span className="invent-modal-store-chip">{selectedStore.name}</span>}
              </div>
              <button className="invent-btn-close" onClick={handleCloseCreateModal}><FaTimes /></button>
            </div>

            <div className="invent-modal-fields-bar invent-desktop-only">
              <div className="invent-form-group">
                <label>Location * <span className="invent-required-hint">(Required)</span></label>
                <select value={formData.locationId || ''} onChange={(e) => setFormData({ ...formData, locationId: parseInt(e.target.value) || null })} disabled={loading || editingId}>
                  <option value="">Select location...</option>
                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </div>
              <div className="invent-form-group">
                <label>Date *</label>
                <input type="date" value={formData.inventoryDate} onChange={(e) => setFormData({ ...formData, inventoryDate: e.target.value })} disabled={loading} />
              </div>
            </div>

            {!editingId && formData.locationId && (
              <div className="invent-load-card-desktop">
                {renderLoadPanel()}
              </div>
            )}

            {renderOpenDraftBanner()}

            <div className="invent-modal-body-scroll">
              <div className="invent-modal-form-mobile">
                <div className="invent-modal-section-card">
                  <div className="invent-modal-section-header">Basic Information</div>
                  <div className="invent-modal-section-body">
                    <div className="invent-modal-field-group">
                      <label className="invent-modal-field-label invent-required">Location</label>
                      <div className="invent-modal-field-input">
                        <select value={formData.locationId || ''} onChange={(e) => setFormData({ ...formData, locationId: parseInt(e.target.value) || null })} disabled={loading || editingId}>
                          <option value="">Select location...</option>
                          {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="invent-modal-field-group">
                      <label className="invent-modal-field-label invent-required">Inventory Date</label>
                      <div className="invent-modal-field-input">
                        <input type="date" value={formData.inventoryDate} onChange={(e) => setFormData({ ...formData, inventoryDate: e.target.value })} disabled={loading} />
                      </div>
                    </div>
                    {!editingId && formData.locationId && (
                      <div className="invent-load-card-mobile">
                        {renderLoadPanel()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {renderProductTable('items', 'Products')}
              {renderPrepTable()}
              {renderProductTable('waste', 'Waste')}
            </div>

            <div className="invent-modal-footer">
              <div className="invent-footer-totals">
                <div className="invent-total-item">
                  <span className="label">WS Total:</span>
                  <span className="value ws">${totals.totalWS.toFixed(2)}</span>
                </div>
                {totals.totalWaste > 0 && (
                  <div className="invent-total-item invent-waste">
                    <span className="label">Waste:</span>
                    <span className="value waste-val">-${totals.totalWaste.toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div className="invent-footer-actions">
                <button className="invent-btn-secondary" onClick={handleCloseCreateModal} disabled={loading}>Cancel</button>
                <button
                  className="invent-btn-save-draft"
                  onClick={handleSave}
                  disabled={loading || blockNewInventory}
                  title={blockNewInventory ? 'Close the open inventory for this location first' : ''}
                >
                  <FaSave /> {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="invent-btn-primary"
                  onClick={handleSaveAndClose}
                  disabled={loading || blockNewInventory}
                  title={blockNewInventory ? 'Close the open inventory for this location first' : ''}
                >
                  {loading ? 'Saving...' : 'Save & Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL UNIFICADO — PRODUCTS + PRE-BATCH ══════════════════════════════ */}
      {showProductModal && (
        <div className="invent-modal-overlay">
          <div className="invent-modal-content invent-modal-picker" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="invent-modal-header">
              <h2>Add Items</h2>
              <button className="invent-btn-close" onClick={() => setShowProductModal(false)}><FaTimes /></button>
            </div>

            {/* Tabs */}
            <div className="invent-picker-tabs">
              <button
                className={`invent-picker-tab ${pickerTab === 'products' ? 'invent-picker-tab--active' : ''}`}
                onClick={() => {
                  setPickerTab('products');
                  setProductSearch('');
                  setPickerExpandedId(null);
                  // Si veníamos del flujo de Pre-Batch, redirigimos el destino
                  // a la tabla de productos correcta ("items"), para que lo que
                  // se agregue aquí no termine metido en prepItems.
                  setSelectingFor(prev => {
                    if (!prev || prev.table === 'prepItems') return { table: 'items', target: null };
                    return prev;
                  });
                }}
                type="button"
              >
                Products
                {Object.values(pickerWeights).filter(pw => pw.currentWeight !== '').length > 0 && (
                  <span className="invent-picker-tab-badge">
                    {Object.values(pickerWeights).filter(pw => pw.currentWeight !== '').length}
                  </span>
                )}
              </button>
              <button
                className={`invent-picker-tab ${pickerTab === 'preps' ? 'invent-picker-tab--active' : ''}`}
                onClick={() => {
                  setPickerTab('preps');
                  setPrepSearch('');
                  setPickerPrepExpandedId(null);
                  setPickerPrepQty({});
                  // Si veníamos del flujo de Productos/Waste, el target (index de esa
                  // tabla) no aplica a prepItems: lo reseteamos para evitar insertar
                  // el prep en una posición equivocada o en la tabla incorrecta.
                  setSelectingFor(prev => {
                    if (!prev || prev.table !== 'prepItems') return { table: 'prepItems', target: null };
                    return prev;
                  });
                }}
                type="button"
              >
                Pre-Batch
              </button>
            </div>

            <div className="invent-modal-body" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>

              {/* ── TAB: PRODUCTS ── */}
              {pickerTab === 'products' && (
                <>
                  <div className="invent-form-group" style={{ flexShrink: 0, marginBottom: 0 }}>
                    <input
                      type="text"
                      placeholder="Search by name or code..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  {filteredProducts.length > 0 && (
                    <p className="invent-picker-hint">
                      Tap a product to enter its weight, then confirm or move to the next one.
                    </p>
                  )}
                  <div className="invent-picker-list" style={{ flex: 1, maxHeight: 'none', minHeight: 60 }}>
                    {filteredProducts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '32px 20px', color: '#94a3b8', fontSize: 14 }}>
                        {productSearch ? 'No products found' : 'No products available'}
                      </div>
                    ) : filteredProducts.map((product, idx) => {
                      const isExpanded = pickerExpandedId === product.id;
                      const pw         = pickerWeights[product.id] || {};
                      const hasFilled  = pw.currentWeight && pw.currentWeight !== '';
                      const isLast     = idx === filteredProducts.length - 1;
                      const containerType = product.containerType || 'Bottle';
                      const containerUnit = product.container_unit || '';
                      const volumeUnits   = ['ml', 'L', 'Liter', 'Gallon', 'fl oz'];
                      const isCount  = countTypes.includes(containerType);
                      const isVolume = !isCount && (volumeUnits.includes(containerUnit) || volumeUnits.includes(containerType));
                      // Sin Full ni Empty Weight → solo Container Type (sin opciones de peso)
                      const noWeights = !product.full_weight && !product.empty_weight;
                      return (
                        <div
                          key={product.id}
                          id={`picker-product-${product.id}`}
                          className={[
                            'invent-picker-product-item',
                            isExpanded ? 'invent-picker-product-item--expanded' : '',
                            hasFilled  ? 'invent-picker-product-item--filled'   : ''
                          ].filter(Boolean).join(' ')}
                        >
                          <div className="invent-picker-product-row" onClick={() => handlePickerToggle(product)}>
                            <div className="invent-picker-product-info">
                              {hasFilled && <span className="invent-picker-filled-dot" title="Weight entered" />}
                              <div className="invent-picker-product-text">
                                <span className="invent-picker-product-name">{product.name}</span>
                                <span className="invent-picker-product-meta">
                                  {product.product_code && <span className="invent-picker-code">{product.product_code}</span>}
                                  {product.container_size && <span>{product.container_size} {containerUnit}</span>}
                                  {product.full_weight && <span className="invent-picker-weights-badge"><FaWeight /> {product.full_weight}g / {product.empty_weight}g</span>}
                                </span>
                              </div>
                            </div>
                            <div className="invent-picker-product-actions-right">
                              {hasFilled && (
                                <span className="invent-picker-weight-preview">
                                  {pw.currentWeight} {pw.weightType}
                                </span>
                              )}
                              <span className={`invent-picker-chevron ${isExpanded ? 'invent-picker-chevron--open' : ''}`}>
                                <FaChevronRight />
                              </span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="invent-picker-inline-fields">
                              <div className="invent-picker-field-group">
                                <label className="invent-picker-field-label">Weight Type</label>
                                <select
                                  className="invent-picker-field-select"
                                  value={noWeights ? containerType : (pw.weightType || (product.full_weight ? 'g' : containerType))}
                                  onChange={(e) => setPickerWeights(prev => ({
                                    ...prev,
                                    [product.id]: { ...prev[product.id], weightType: e.target.value }
                                  }))}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {noWeights
                                    ? (<option value={containerType}>{containerType}</option>)
                                    : (<>
                                        {isCount  && <><option value={containerType}>{containerType} (container)</option><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>}
                                        {isVolume && <><option value={containerType}>{containerType} (container)</option><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>}
                                        {!isCount && !isVolume && <><option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option></>}
                                      </>)}
                                </select>
                              </div>
                              <div className="invent-picker-field-group">
                                <label className="invent-picker-field-label">Current Weight *</label>
                                <input
                                  type="number" step="1" placeholder="0"
                                  className="invent-picker-field-input"
                                  value={pw.currentWeight || ''}
                                  onChange={(e) => setPickerWeights(prev => ({
                                    ...prev,
                                    [product.id]: { ...prev[product.id], currentWeight: e.target.value }
                                  }))}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                              </div>
                              <div className="invent-picker-inline-actions">
                                <button
                                  className="invent-picker-btn-confirm"
                                  onClick={(e) => { e.stopPropagation(); handleConfirmSingleProduct(product); }}
                                  disabled={!pw.currentWeight || pw.currentWeight === ''}
                                >
                                  ✓ Add
                                </button>
                                {!isLast && (
                                  <button
                                    className="invent-picker-btn-next"
                                    onClick={(e) => { e.stopPropagation(); handlePickerNext(product); }}
                                  >
                                    Next <FaChevronRight />
                                  </button>
                                )}
                                <button
                                  className="invent-picker-btn-skip"
                                  onClick={(e) => { e.stopPropagation(); handleConfirmSingleProduct(product); }}
                                >
                                  Add without weight
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* ── TAB: PRE-BATCH ── */}
              {pickerTab === 'preps' && (
                <>
                  <div className="invent-form-group" style={{ flexShrink: 0, marginBottom: 0 }}>
                    <input
                      type="text"
                      placeholder="Search pre-batch by name..."
                      value={prepSearch}
                      onChange={(e) => setPrepSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  {filteredPreps.length > 0 && (
                    <p className="invent-picker-hint">
                      Tap a pre-batch to enter its quantity, then confirm or move to the next one.
                    </p>
                  )}
                  <div className="invent-picker-list" style={{ flex: 1, maxHeight: 'none', minHeight: 60 }}>
                    {filteredPreps.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '32px 20px', color: '#94a3b8', fontSize: 14 }}>
                        {prepSearch ? 'No pre-batches found' : 'No pre-batches available'}
                      </div>
                    ) : filteredPreps.map((prep, idx) => {
                      const isExpanded  = pickerPrepExpandedId === prep.id;
                      const pq          = pickerPrepQty[prep.id] || {};
                      const hasFilled   = pq.currentQty && pq.currentQty !== '';
                      const isLast      = idx === filteredPreps.length - 1;
                      return (
                        <div
                          key={prep.id}
                          id={`picker-prep-${prep.id}`}
                          className={[
                            'invent-picker-product-item',
                            isExpanded ? 'invent-picker-product-item--expanded' : '',
                            hasFilled  ? 'invent-picker-product-item--filled'   : ''
                          ].filter(Boolean).join(' ')}
                        >
                          {/* Row principal */}
                          <div className="invent-picker-product-row" onClick={() => handlePickerPrepToggle(prep)}>
                            <div className="invent-picker-product-info">
                              {hasFilled && <span className="invent-picker-filled-dot" title="Quantity entered" />}
                              <div className="invent-picker-product-text">
                                <span className="invent-picker-product-name">{prep.name}</span>
                                <span className="invent-picker-product-meta">
                                  <span>Yield: {prep.yield_quantity} {prep.yield_unit}</span>
                                  <span className="invent-picker-code">
                                    ${prep.yield_unit_cost ? parseFloat(prep.yield_unit_cost).toFixed(4) : '0.0000'} / {prep.yield_unit || 'unit'}
                                  </span>
                                  <span>Total: ${parseFloat(prep.total_cost || 0).toFixed(2)}</span>
                                </span>
                              </div>
                            </div>
                            <div className="invent-picker-product-actions-right">
                              {hasFilled && (
                                <span className="invent-picker-weight-preview">
                                  {pq.currentQty} {prep.yield_unit}
                                </span>
                              )}
                              <span className={`invent-picker-chevron ${isExpanded ? 'invent-picker-chevron--open' : ''}`}>
                                <FaChevronRight />
                              </span>
                            </div>
                          </div>

                          {/* Panel expandido */}
                          {isExpanded && (
                            <div className="invent-picker-inline-fields">
                              <div className="invent-picker-field-group">
                                <label className="invent-picker-field-label">Unit</label>
                                <div className="invent-picker-field-unit-badge">
                                  {prep.yield_unit || 'Each'}
                                </div>
                              </div>
                              <div className="invent-picker-field-group">
                                <label className="invent-picker-field-label">Current Qty *</label>
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  placeholder="0"
                                  className="invent-picker-field-input"
                                  value={pq.currentQty || ''}
                                  onChange={(e) => setPickerPrepQty(prev => ({
                                    ...prev,
                                    [prep.id]: { ...prev[prep.id], currentQty: e.target.value }
                                  }))}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                              </div>
                              <div className="invent-picker-inline-actions">
                                <button
                                  className="invent-picker-btn-confirm"
                                  onClick={(e) => { e.stopPropagation(); handleConfirmSinglePrep(prep); }}
                                  disabled={!pq.currentQty || pq.currentQty === ''}
                                >
                                  ✓ Add
                                </button>
                                {!isLast && (
                                  <button
                                    className="invent-picker-btn-next"
                                    onClick={(e) => { e.stopPropagation(); handlePickerPrepNext(prep); }}
                                  >
                                    Next <FaChevronRight />
                                  </button>
                                )}
                                <button
                                  className="invent-picker-btn-skip"
                                  onClick={(e) => { e.stopPropagation(); handleConfirmSinglePrep(prep); }}
                                >
                                  Add without qty
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="invent-modal-footer" style={{ flexShrink: 0 }}>
              <span className="invent-picker-footer-hint">
                {pickerTab === 'products'
                  ? (pickerWithWeightCount > 0
                      ? `${pickerWithWeightCount} product${pickerWithWeightCount > 1 ? 's' : ''} with weight entered`
                      : 'Tap a product to expand and enter its weight')
                  : (Object.values(pickerPrepQty).filter(pq => pq.currentQty && pq.currentQty !== '').length > 0
                      ? `${Object.values(pickerPrepQty).filter(pq => pq.currentQty && pq.currentQty !== '').length} pre-batch(es) with qty entered`
                      : 'Tap a pre-batch to expand and enter its quantity')
                }
              </span>
              <div className="invent-footer-actions">
                <button className="invent-btn-secondary" onClick={() => setShowProductModal(false)}>Cancel</button>
                {pickerTab === 'products' && pickerWithWeightCount > 0 && (
                  <button className="invent-btn-primary" onClick={handleAddAllWithWeight}>
                    <FaPlus /> Add {pickerWithWeightCount} product{pickerWithWeightCount > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

            {/* ══ MODAL DELETE ══════════════════════════════════════════════════════ */}
      {showDeleteModal && (
        <div className="invent-modal-overlay">
          <div className="invent-modal-content invent-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="invent-modal-header">
              <h2>Confirm deletion</h2>
              <button className="invent-btn-close" onClick={handleCloseDeleteModal}><FaTimes /></button>
            </div>
            <div className="invent-modal-body">
              <p>Are you sure you want to delete this inventory?</p>
              <p className="invent-warning-text">This action cannot be undone.</p>
            </div>
            <div className="invent-modal-footer">
              <button className="invent-btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>Cancel</button>
              <button className="invent-btn-danger" onClick={handleConfirmDelete} disabled={loading}>{loading ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL PRINT ══════════════════════════════════════════════════════ */}
      {showPrintModal && (
        <div className="invent-modal-overlay">
          <div className="invent-modal-content invent-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="invent-modal-header">
              <h2>Print Inventory Format</h2>
              <button className="invent-btn-close" onClick={() => setShowPrintModal(false)}><FaTimes /></button>
            </div>
            <div className="invent-modal-body">
              <div className="invent-form-group">
                <label>Select Location *</label>
                <select value={printLocationId} onChange={(e) => setPrintLocationId(e.target.value)} disabled={printExportLoading}>
                  <option value="">Select a location...</option>
                  {locations.filter(l =>
                    l.name.toLowerCase() !== 'physical inventory' &&
                    l.name.toLowerCase() !== 'from excel'
                  ).map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </div>
              <div style={{ marginTop: 16 }}>
                <GroupByToggle value={groupBy} onChange={setGroupBy} />
              </div>
              <p style={{ fontSize: 13, color: '#64748b', marginTop: 12 }}>
                Will use the most recent locked inventory for the selected location.
              </p>
            </div>
            <div className="invent-modal-footer">
              <button className="invent-btn-secondary" onClick={() => setShowPrintModal(false)} disabled={printExportLoading}>Cancel</button>
              <button className="invent-btn-excel" onClick={() => handlePrintExport('csv')} disabled={printExportLoading || !printLocationId}>
                <FaFileExcel /> Excel (CSV)
              </button>
              <button className="invent-btn-primary" onClick={() => handlePrintExport('pdf')} disabled={printExportLoading || !printLocationId}>
                <FaFilePdf /> {printExportLoading ? 'Exporting...' : 'PDF / Print'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Inventories;